import { useState, useEffect, useCallback, useRef } from "react";
import { LineChart, Line, AreaChart, Area, ReferenceLine, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts"; // EQ-CURVE v2
import { TrendingUp, Zap, Brain, Briefcase, BookOpen, Settings as SettingsIcon, Crown } from "lucide-react";

const API_BASE = "";
const REFRESH_MS = 5 * 60 * 1000;
const BITGET_REFRESH_MS = 30 * 1000;
const TOKEN_KEY = "scanner_token";
const USER_KEY = "scanner_user";

function getToken() { try { return localStorage.getItem(TOKEN_KEY); } catch { return null; } }
function setToken(t, u) { try { localStorage.setItem(TOKEN_KEY, t); localStorage.setItem(USER_KEY, u); } catch {} }
function clearToken() { try { localStorage.removeItem(TOKEN_KEY); localStorage.removeItem(USER_KEY); } catch {} }
function getUser() { try { return localStorage.getItem(USER_KEY) || ""; } catch { return ""; } }

async function api(ep, opts = {}) {
  const token = getToken();
  const headers = { "Content-Type": "application/json", ...(opts.headers || {}) };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  try {
    const r = await fetch(`${API_BASE}${ep}`, { ...opts, headers });
    const json = await r.json();
    if (r.status === 401) { clearToken(); json._authFailed = true; }
    return json;
  } catch (e) { return { ok: false, error: e.message }; }
}

const fU = n => { if (n == null || isNaN(n)) return "—"; if (Math.abs(n) >= 1000) return "$" + n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }); if (Math.abs(n) >= 1) return "$" + n.toFixed(2); if (Math.abs(n) >= 0.01) return "$" + n.toFixed(4); return "$" + n.toFixed(6); };
const fP = n => n == null ? "—" : (n >= 0 ? "+" : "") + (n * 100).toFixed(2) + "%";
const fPR = n => n == null ? "—" : (n >= 0 ? "+" : "") + n.toFixed(2) + "%";
const fV = n => { if (n >= 1e9) return "$" + (n / 1e9).toFixed(2) + "B"; if (n >= 1e6) return "$" + (n / 1e6).toFixed(0) + "M"; return "$" + n?.toLocaleString(); };
const cleanSym = s => (s || "").replace("USDT", "");

const C = {
  bg: "#06090f", card: "#0c1119", inner: "#080d14", bdr: "#151e2d", bdrH: "#1e2d42",
  tx: "#e2e8f0", dim: "#9aa5b8", mut: "#5a6b85",
  or: "#f7931a", bl: "#3b82f6", gn: "#10b981", rd: "#ef4444",
  yl: "#eab308", pu: "#a78bfa", cy: "#22d3ee",
  mono: "'JetBrains Mono', 'IBM Plex Mono', monospace",
  sans: "'DM Sans', 'IBM Plex Sans', sans-serif",
  display: "'Space Grotesk', 'DM Sans', sans-serif",
};

// ─── Login Screen ────────────────────────────────────
function LoginScreen({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) { setError("Enter username and password"); return; }
    setLoading(true); setError("");
    const res = await api("/api/login", { method: "POST", body: JSON.stringify({ username, password }) });
    setLoading(false);
    if (res.ok) { setToken(res.token, res.username); onLogin(res.username, res.role); }
    else setError(res.error || "Login failed");
  };

  const iS = { width: "100%", padding: "14px 16px", background: C.inner, border: `1px solid ${C.bdr}`, borderRadius: 10, color: C.tx, fontFamily: C.mono, fontSize: 13, outline: "none", boxSizing: "border-box", transition: "border-color 0.2s" };

  return (
    <div style={{ width: "100%", minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, position: "relative", overflow: "hidden" }}>
      {/* Grid background */}
      <div style={{ position: "absolute", inset: 0, backgroundImage: `linear-gradient(${C.bdr}40 1px, transparent 1px), linear-gradient(90deg, ${C.bdr}40 1px, transparent 1px)`, backgroundSize: "60px 60px", opacity: 0.3 }} />
      {/* Glow */}
      <div style={{ position: "absolute", top: "20%", left: "50%", transform: "translateX(-50%)", width: 400, height: 400, borderRadius: "50%", background: `radial-gradient(circle, ${C.or}08, transparent 70%)` }} />

      <div style={{ width: "100%", maxWidth: 400, position: "relative", zIndex: 1 }}>
        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 64, height: 64, borderRadius: 20, background: `linear-gradient(135deg, ${C.or}20, ${C.or}05)`, border: `1px solid ${C.or}30`, marginBottom: 20, fontSize: 28, boxShadow: `0 0 40px ${C.or}15` }}>⚡</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: C.tx, fontFamily: C.display, letterSpacing: "-0.5px" }}>
            bit<span style={{ color: C.or }}>scanner</span>
          </div>
          <div style={{ fontSize: 12, color: C.dim, marginTop: 8, fontFamily: C.sans, letterSpacing: "2px", textTransform: "uppercase" }}>Crypto · Stocks · AI-Powered</div>
            <div style={{ fontSize: 11, color: C.mut, marginTop: 12, fontFamily: C.sans, lineHeight: 1.6 }}>Private invite-only dashboard for personal position monitoring and trade tracking.</div>
        </div>
        <div style={{ background: C.card, border: `1px solid ${C.bdr}`, borderRadius: 16, padding: 32, boxShadow: "0 20px 60px rgba(0,0,0,0.5)" }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: C.dim, marginBottom: 24, fontFamily: C.sans }}>Sign in</div>
          <div style={{ marginBottom: 18 }}>
            <label style={{ fontSize: 10, color: C.mut, textTransform: "uppercase", letterSpacing: "1.5px", display: "block", marginBottom: 8, fontFamily: C.sans, fontWeight: 600 }}>Username</label>
            <input type="text" value={username} onChange={e => setUsername(e.target.value)} placeholder="Enter username" autoCapitalize="none" autoCorrect="off" style={iS} />
          </div>
          <div style={{ marginBottom: 24 }}>
            <label style={{ fontSize: 10, color: C.mut, textTransform: "uppercase", letterSpacing: "1.5px", display: "block", marginBottom: 8, fontFamily: C.sans, fontWeight: 600 }}>Password</label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Enter password" onKeyDown={e => e.key === "Enter" && handleSubmit(e)} style={iS} />
          </div>
          {error && <div style={{ padding: "10px 14px", background: `${C.rd}10`, border: `1px solid ${C.rd}25`, borderRadius: 10, fontSize: 12, color: C.rd, marginBottom: 18, fontFamily: C.sans }}>{error}</div>}
          <button onClick={handleSubmit} disabled={loading} style={{ width: "100%", padding: 15, background: loading ? C.inner : `linear-gradient(135deg, ${C.or}, #e8850f)`, border: "none", borderRadius: 10, color: loading ? C.dim : "#000", fontSize: 13, fontWeight: 700, cursor: loading ? "wait" : "pointer", fontFamily: C.mono, textTransform: "uppercase", letterSpacing: "1px", boxShadow: loading ? "none" : `0 4px 20px ${C.or}30`, transition: "all 0.2s" }}>{loading ? "Signing in..." : "Sign In"}</button>
        </div>
        <div style={{ textAlign: "center", marginTop: 24, fontSize: 10, color: C.mut, fontFamily: C.sans, letterSpacing: "1px" }}>HTTPS ENCRYPTED · v1.16</div>
        <div style={{ textAlign: "center", marginTop: 12, fontSize: 9, color: C.mut, fontFamily: C.sans, lineHeight: 1.6, maxWidth: 340, margin: "12px auto 0" }}>Trading involves substantial risk of loss. Past performance does not guarantee future results. Users trade on their own accounts with their own funds. This platform provides tools, not financial advice.</div>
        <div style={{ textAlign: "center", marginTop: 16, fontSize: 10, color: C.mut, fontFamily: C.sans, letterSpacing: "1px" }}>
          <a href="/about.html" style={{ color: C.mut, textDecoration: "none", margin: "0 8px" }}>About</a>
          <span style={{ color: C.bdr }}>·</span>
          <a href="/privacy.html" style={{ color: C.mut, textDecoration: "none", margin: "0 8px" }}>Privacy</a>
          <span style={{ color: C.bdr }}>·</span>
          <a href="/terms.html" style={{ color: C.mut, textDecoration: "none", margin: "0 8px" }}>Terms</a>
        </div>
      </div>
    </div>
  );
}

// ─── Components ──────────────────────────────────────
const Badge = ({ type, children }) => {
  const s = { long: [C.gn, C.gn], short: [C.rd, C.rd], win: [C.gn, C.gn], loss: [C.rd, C.rd], neutral: [C.yl, C.yl], info: [C.bl, C.bl], exchange: [C.pu, C.pu], stock: ["#ff6b35", "#ff6b35"], crypto: [C.cy, C.cy], live: [C.gn, C.gn], active: [C.or, C.or], tf: [C.bl, C.bl], ob: [C.pu, C.pu] }[type] || [C.bl, C.bl];
  return <span style={{ display: "inline-flex", padding: "2px 7px", background: s[0] + "12", border: `1px solid ${s[1]}35`, color: s[1], borderRadius: 5, fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.3px", fontFamily: C.mono, whiteSpace: "nowrap" }}>{children}</span>;
};

const Conf = ({ s }) => {
  const c = s >= 8 ? C.gn : s >= 6 ? C.yl : C.rd;
  return <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
    <div style={{ display: "flex", gap: 1.5 }}>{Array.from({ length: 10 }).map((_, i) => <div key={i} style={{ width: 4, height: 14, borderRadius: 2, background: i < s ? c : `${C.bdr}80`, transition: "background 0.3s" }} />)}</div>
    <span style={{ fontSize: 11, color: c, fontWeight: 700, fontFamily: C.mono }}>{s}</span>
  </div>;
};

const Stat = ({ label, value, sub, color, icon }) => (
  <div style={{ background: C.card, border: `1px solid ${C.bdr}`, borderRadius: 10, padding: "12px 14px", flex: 1, minWidth: 90 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 6 }}>
      {icon && <span style={{ fontSize: 12 }}>{icon}</span>}
      <span style={{ fontSize: 9, color: C.mut, textTransform: "uppercase", letterSpacing: "1px", fontFamily: C.sans, fontWeight: 600 }}>{label}</span>
    </div>
    <div style={{ fontSize: 18, fontWeight: 700, color: color || C.tx, fontFamily: C.mono }}>{value}</div>
    {sub && <div style={{ fontSize: 9, color: C.dim, marginTop: 3, fontFamily: C.sans }}>{sub}</div>}
  </div>
);

const Card = ({ children, accent, style: sx, glow }) => (
  <div style={{ background: C.card, border: `1px solid ${C.bdr}`, borderRadius: 12, padding: 16, position: "relative", overflow: "hidden", ...(glow ? { boxShadow: `0 0 30px ${glow}10` } : {}), ...sx }}>
    {accent && <div style={{ position: "absolute", top: 0, left: 0, width: 3, height: "100%", background: accent, borderRadius: "3px 0 0 3px" }} />}
    {children}
  </div>
);

const Stitle = ({ icon, children, color, right }) => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: color || C.or, textTransform: "uppercase", letterSpacing: "1.5px", fontWeight: 700, fontFamily: C.sans }}>
      {icon && <span style={{ fontSize: 13 }}>{icon}</span>}{children}
    </div>
    {right && <div>{right}</div>}
  </div>
);

const Loader = ({ text }) => (
  <div style={{ padding: 60, textAlign: "center" }}>
    <div style={{ display: "flex", justifyContent: "center", gap: 6, marginBottom: 14 }}>
      {[0, 1, 2, 3].map(i => <div key={i} style={{ width: 8, height: 8, borderRadius: "50%", background: C.or, animation: `pulse 1.2s infinite ${i * 0.15}s` }} />)}
    </div>
    <div style={{ fontSize: 12, color: C.dim, fontFamily: C.sans }}>{text}</div>
  </div>
);

const AssetFilter = ({ value, onChange, hasNewsOpps }) => (
  <div style={{ display: "flex", gap: 4, marginBottom: 14 }}>
    {[{ k: "all", l: "All", icon: "📊" }, { k: "crypto", l: "Crypto", icon: "🪙" }, { k: "stocks", l: "Stocks", icon: "📈" },
      ...(hasNewsOpps ? [{ k: "news", l: "News Opps", icon: "⚡" }] : [])
    ].map(({ k, l, icon }) => (
      <button key={k} onClick={() => onChange(k)} style={{
        flex: 1, padding: "9px 6px", background: value === k ? (k === "news" ? `${C.rd}15` : `${C.or}12`) : C.inner,
        border: `1px solid ${value === k ? (k === "news" ? C.rd : C.or) : C.bdr}`, borderRadius: 8,
        color: value === k ? (k === "news" ? C.rd : C.or) : C.dim, cursor: "pointer",
        fontFamily: C.sans, fontSize: 11, fontWeight: value === k ? 700 : 500,
        display: "flex", alignItems: "center", justifyContent: "center", gap: 4, transition: "all 0.2s",
      }}>{icon} {l}</button>
    ))}
  </div>
);

// ─── Fear & Greed Gauge ──────────────────────────────
function FearGreedGauge({ value, label }) {
  const angle = value ? (value / 100) * 180 - 90 : -90;
  const color = value < 20 ? C.rd : value < 40 ? "#ff8800" : value < 60 ? C.yl : value < 80 ? C.gn : "#00ff88";
  return (
    <div style={{ textAlign: "center" }}>
      <svg viewBox="0 0 200 120" style={{ width: "100%", maxWidth: 180 }}>
        {/* Background arc */}
        <path d="M 20 100 A 80 80 0 0 1 180 100" fill="none" stroke={C.bdr} strokeWidth="12" strokeLinecap="round" />
        {/* Colored arc */}
        <path d="M 20 100 A 80 80 0 0 1 180 100" fill="none" stroke={color} strokeWidth="12" strokeLinecap="round"
          strokeDasharray={`${(value || 0) / 100 * 251.2} 251.2`} style={{ transition: "stroke-dasharray 1s ease, stroke 1s ease" }} />
        {/* Needle */}
        <line x1="100" y1="100" x2={100 + 55 * Math.cos(angle * Math.PI / 180)} y2={100 + 55 * Math.sin(angle * Math.PI / 180)}
          stroke={C.tx} strokeWidth="2" strokeLinecap="round" style={{ transition: "all 1s ease" }} />
        <circle cx="100" cy="100" r="4" fill={C.tx} />
        {/* Value */}
        <text x="100" y="85" textAnchor="middle" fill={color} fontSize="28" fontWeight="800" fontFamily={C.mono}>{value || "—"}</text>
        <text x="100" y="115" textAnchor="middle" fill={C.dim} fontSize="10" fontFamily={C.sans}>{label || ""}</text>
      </svg>
    </div>
  );
}

// ─── Market Tab ──────────────────────────────────────
function MarketTab({ scan, loading }) {
  const [filter, setFilter] = useState("all");
  if (loading) return <Loader text="Loading market data..." />;
  if (!scan) return <Card><div style={{ textAlign: "center", color: C.dim, padding: 20 }}>No data yet — waiting for scan</div></Card>;
  const pairs = filter === "crypto" ? (scan.cryptoPairs || []) : filter === "stocks" ? (scan.stockPairs || []) : (scan.topPairs || []);
  const ms = scan.marketSentiment;
  const fg = ms?.fearGreed;

  return (
    <div>
      {/* MARKET-TAB-REGIME-HEADER-V1 */}
      <Card style={{ marginBottom: 14, position: "relative", overflow: "hidden" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 11, color: C.mut, fontFamily: C.sans, textTransform: "uppercase", letterSpacing: "0.1em" }}>Market regime</div>
            <div style={{ fontSize: 26, fontWeight: 700, color: fg?.value == null ? C.dim : fg.value < 25 ? C.rd : fg.value < 45 ? "#ff8800" : fg.value < 55 ? C.yl : C.gn, fontFamily: C.display, marginTop: 3, lineHeight: 1 }}>
              {fg?.label || "—"}
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 34, fontWeight: 800, color: fg?.value == null ? C.dim : fg.value < 25 ? C.rd : fg.value < 45 ? "#ff8800" : fg.value < 55 ? C.yl : C.gn, fontFamily: C.mono, lineHeight: 1 }}>{fg?.value != null ? fg.value : "—"}</div>
            <div style={{ fontSize: 9, color: C.mut, fontFamily: C.mono, textTransform: "uppercase", letterSpacing: "0.08em", marginTop: 4 }}>Fear &amp; Greed</div>
          </div>
        </div>
        <div style={{ height: 6, borderRadius: 6, background: "linear-gradient(90deg, #ef4444, #eab308 45%, #10b981)", position: "relative", marginBottom: 14 }}>
          <div style={{ position: "absolute", top: -3, left: (fg?.value != null ? Math.max(2, Math.min(98, fg.value)) : 50) + "%", width: 3, height: 12, background: "#fff", borderRadius: 2, boxShadow: "0 0 6px rgba(255,255,255,0.7)", transition: "left 1s ease" }} />
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {[
            { k: "Sentiment", v: ms?.sentimentLabel || "—", dot: ms?.sentimentLabel === "Bullish" ? C.gn : ms?.sentimentLabel === "Bearish" ? C.rd : C.yl },
            { k: "Articles", v: ms ? String(ms.totalArticles) : "—", dot: C.mut },
            { k: "Signals", v: String(scan.qualifiedCount), dot: scan.qualifiedCount > 0 ? C.gn : C.yl },
          ].map((chip, i) => (
            <div key={i} style={{ flex: 1, minWidth: 90, background: C.inner, border: "1px solid " + C.bdr, borderRadius: 10, padding: "9px 10px" }}>
              <div style={{ fontSize: 9, color: C.mut, fontFamily: C.mono, textTransform: "uppercase", letterSpacing: "0.06em" }}>{chip.k}</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: C.tx, fontFamily: C.mono, marginTop: 3, display: "flex", alignItems: "center", gap: 5 }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: chip.dot, display: "inline-block" }} />{chip.v}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {ms?.breakingAlerts?.length > 0 && (
        <Card style={{ marginBottom: 14, borderColor: `${C.rd}30` }} glow={C.rd}>
          <Stitle icon="🚨" color={C.rd}>Breaking News</Stitle>
          {ms.breakingAlerts.map((a, i) => (
            <div key={i} style={{ fontSize: 11, color: a.score < 0 ? C.rd : a.score > 0 ? C.gn : C.dim, padding: "4px 0", fontFamily: C.sans, display: "flex", gap: 6 }}>
              <span style={{ color: C.rd, fontWeight: 700 }}>⚡</span><span>{a.title}</span>
            </div>
          ))}
        </Card>
      )}
      {ms?.topHeadlines?.length > 0 && (
        <Card style={{ marginBottom: 14 }}>
          <Stitle icon="📰">Headlines</Stitle>
          {ms.topHeadlines.slice(0, 6).map((h, i) => (
            <div key={i} style={{ fontSize: 11, padding: "6px 0", borderBottom: i < 5 ? `1px solid ${C.inner}` : "none", display: "flex", justifyContent: "space-between", gap: 8 }}>
              <div style={{ color: C.dim, fontFamily: C.sans, flex: 1 }}>{h.isBreaking && <span style={{ color: C.rd, fontWeight: 700, marginRight: 4 }}>⚡</span>}{h.title}</div>
              <div style={{ flexShrink: 0, padding: "2px 6px", borderRadius: 4, fontSize: 9, fontWeight: 700, fontFamily: C.mono, background: h.score > 10 ? `${C.gn}15` : h.score < -10 ? `${C.rd}15` : `${C.yl}15`, color: h.score > 10 ? C.gn : h.score < -10 ? C.rd : C.yl }}>{h.score > 0 ? "+" : ""}{h.score}</div>
            </div>
          ))}
        </Card>
      )}
      <AssetFilter value={filter} onChange={setFilter} />
      <Card>
        <Stitle icon={filter === "stocks" ? "📈" : "🪙"}>{filter === "stocks" ? "Stock" : filter === "crypto" ? "Crypto" : "All"} Futures</Stitle>
        <div style={{ overflowX: "auto", margin: "0 -16px", padding: "0 16px" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11, fontFamily: C.mono, minWidth: 500 }}>
            <thead><tr style={{ borderBottom: `2px solid ${C.bdr}` }}>
              {["#", "Pair", "Price", "24h", "Volume"].map(h => (
                <th key={h} style={{ padding: "10px 6px", textAlign: "left", color: C.mut, fontSize: 9, textTransform: "uppercase", fontFamily: C.sans, fontWeight: 600, letterSpacing: "1px" }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {pairs.slice(0, 100).map((p, i) => (
                <tr key={p.symbol + (p.exchange || "")} style={{ borderBottom: `1px solid ${C.inner}`, transition: "background 0.15s" }}>
                  <td style={{ padding: "8px 6px", color: C.mut, fontSize: 9 }}>{i + 1}</td>
                  <td style={{ padding: "8px 6px" }}><div style={{ display: "flex", alignItems: "center", gap: 5 }}><span style={{ fontWeight: 700, color: C.tx }}>{cleanSym(p.symbol)}</span><Badge type={p.assetType === "Stock" ? "stock" : "exchange"}>{p.assetType === "Stock" ? "STOCK" : (p.exchange === "Bybit" ? "BYB" : "BIT")}</Badge></div></td>
                  <td style={{ padding: "8px 6px", color: C.tx }}>{fU(p.price)}</td>
                  <td style={{ padding: "8px 6px", color: p.change24h >= 0 ? C.gn : C.rd, fontWeight: 600 }}>{fP(p.change24h)}</td>
                  <td style={{ padding: "8px 6px", color: C.dim }}>{fV(p.volume24h)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// ─── Signals Tab ─────────────────────────────────────
function SignalsTab({ scan, loading, onExec, openCount, maxOpen }) {
  const [filter, setFilter] = useState("all");
  if (loading) return <Loader text="Generating signals..." />;
  if (!scan) return <Card><div style={{ textAlign: "center", color: C.dim, padding: 20 }}>Run a scan</div></Card>;
  const allSetups = scan.setups || [];
  const newsOpps = scan.newsOpportunities || [];
  const setups = filter === "crypto" ? (scan.cryptoSetups || []) : filter === "stocks" ? (scan.stockSetups || []) : filter === "news" ? newsOpps : allSetups;

  return (
    <div>
      <Card style={{ marginBottom: 14 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 11, color: C.dim, fontFamily: C.sans }}>
              <span style={{ color: C.tx, fontWeight: 700, fontSize: 20, fontFamily: C.mono }}>{allSetups.length}</span>
              <span style={{ marginLeft: 6 }}>signals from {scan.scannedCount} pairs</span>
            </div>
            <div style={{ fontSize: 10, color: C.mut, fontFamily: C.sans, marginTop: 2 }}>
              {(scan.cryptoSetups || []).length} crypto · {(scan.stockSetups || []).length} stocks{newsOpps.length > 0 ? ` · ${newsOpps.length} news` : ""}
            </div>
          </div>
          {openCount >= maxOpen && <Badge type="loss">MAX {maxOpen} TRADES</Badge>}
        </div>
      </Card>
      <AssetFilter value={filter} onChange={setFilter} hasNewsOpps={newsOpps.length > 0} />
      {setups.length === 0 ? (
        <Card style={{ textAlign: "center", padding: 50 }}>
          <div style={{ fontSize: 32, marginBottom: 10, opacity: 0.5 }}>📭</div>
          <div style={{ fontSize: 14, color: C.dim, fontWeight: 600, fontFamily: C.sans }}>No signals for {filter}</div>
          <div style={{ fontSize: 11, color: C.mut, marginTop: 4, fontFamily: C.sans }}>Capital protection active</div>
        </Card>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {setups.map((s, i) => (
            <Card key={i} accent={s.signalType === "NEWS_OPPORTUNITY" ? C.yl : s.direction === "LONG" ? C.gn : C.rd}>
              {s.signalType === "NEWS_OPPORTUNITY" && (
                <div style={{ background: `${C.yl}10`, border: `1px solid ${C.yl}25`, borderRadius: 8, padding: "8px 12px", marginBottom: 12, display: "flex", alignItems: "flex-start", gap: 8 }}>
                  <span style={{ fontSize: 14 }}>⚡</span>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: C.yl, fontFamily: C.sans }}>NEWS OPPORTUNITY — {s.newsLabel}</div>
                    <div style={{ fontSize: 9, color: C.dim, fontFamily: C.sans, marginTop: 2 }}>{s.overreaction?.priceMove?.toFixed(1)}% move · {s.overreaction?.magnitude?.toFixed(1)}x ATR</div>
                  </div>
                </div>
              )}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 5, flexWrap: "wrap", marginBottom: 4 }}>
                    <span style={{ fontSize: 16, fontWeight: 800, color: C.tx, fontFamily: C.mono }}>{cleanSym(s.symbol)}</span>
                    <Badge type={s.direction === "LONG" ? "long" : "short"}>{s.direction}</Badge>
                    <Badge type="info">{s.leverage}x</Badge>
                    <Badge type={s.assetType === "Stock" ? "stock" : "crypto"}>{s.assetType}</Badge>
                  </div>
                  <div style={{ fontSize: 10, color: C.mut, fontFamily: C.sans }}>{s.exchange} · {fV(s.volume24h)}</div>
                </div>
                <Conf s={s.confidence} />
              </div>

              {/* Tier 1 data badges */}
              {(s.confluence || s.orderBook) && (
                <div style={{ display: "flex", gap: 4, marginBottom: 10, flexWrap: "wrap" }}>
                  {s.confluence && <Badge type="tf">TF {s.confluence.score}%</Badge>}
                  {s.orderBook && s.orderBook.imbalance !== "neutral" && <Badge type="ob">OB {s.orderBook.imbalance} ({s.orderBook.ratio})</Badge>}
                  {s.orderBook && s.orderBook.imbalance === "neutral" && <Badge type="info">OB Neutral</Badge>}
                </div>
              )}

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 12, background: C.inner, borderRadius: 10, padding: 12, border: `1px solid ${C.bdr}` }}>
                {[{ l: "Entry", v: fU(s.entry), c: C.tx }, { l: "Stop Loss", v: fU(s.stopLoss), c: C.rd }, { l: "TP1", v: fU(s.tp1), c: C.gn }, { l: "TP2", v: fU(s.tp2), c: C.gn }, { l: "TP3", v: fU(s.tp3), c: C.gn }, { l: "Risk", v: `${s.riskPct}% · $${s.riskAmount}`, c: C.yl }].map(({ l, v, c }) => (
                  <div key={l}><div style={{ fontSize: 8, color: C.mut, textTransform: "uppercase", fontFamily: C.sans, marginBottom: 3, letterSpacing: "0.5px" }}>{l}</div><div style={{ fontSize: 12, color: c, fontWeight: 700, fontFamily: C.mono }}>{v}</div></div>
                ))}
              </div>
              {s.newsSentiment && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, padding: "8px 12px", background: C.inner, borderRadius: 8, border: `1px solid ${C.bdr}`, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 11 }}>📰</span>
                  <Badge type={s.newsSentiment.score > 20 ? "long" : s.newsSentiment.score < -20 ? "short" : "neutral"}>{s.newsSentiment.label} ({s.newsSentiment.score > 0 ? "+" : ""}{s.newsSentiment.score})</Badge>
                  {s.newsSentiment.fearGreed != null && <span style={{ fontSize: 9, color: C.dim, fontFamily: C.mono }}>F&G: {s.newsSentiment.fearGreed}</span>}
                  {s.newsSentiment.modifier !== 0 && <span style={{ fontSize: 9, color: s.newsSentiment.modifier > 0 ? C.gn : C.rd, fontFamily: C.mono, fontWeight: 700 }}>Conf {s.newsSentiment.modifier > 0 ? "+" : ""}{s.newsSentiment.modifier}</span>}
                </div>
              )}
              <details style={{ marginBottom: 12 }}>
                <summary style={{ fontSize: 9, color: C.mut, textTransform: "uppercase", fontFamily: C.sans, cursor: "pointer", userSelect: "none", letterSpacing: "0.5px" }}>Confirmations ({s.confirmations.length})</summary>
                <div style={{ marginTop: 6 }}>
                  {s.confirmations.map((c, j) => <div key={j} style={{ fontSize: 10, color: C.dim, padding: "2px 0", fontFamily: C.sans }}><span style={{ color: C.or }}>›</span> {c}</div>)}
                </div>
              </details>
              <button onClick={() => onExec(s)} disabled={openCount >= maxOpen} style={{
                width: "100%", padding: 12, background: openCount >= maxOpen ? C.inner : s.direction === "LONG" ? `${C.gn}10` : `${C.rd}10`,
                border: `1px solid ${openCount >= maxOpen ? C.bdr : s.direction === "LONG" ? C.gn : C.rd}`,
                color: openCount >= maxOpen ? C.mut : s.direction === "LONG" ? C.gn : C.rd,
                borderRadius: 10, cursor: openCount >= maxOpen ? "not-allowed" : "pointer",
                fontFamily: C.mono, fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", transition: "all 0.2s",
              }}>{openCount >= maxOpen ? "Max trades reached" : `Execute ${s.direction} · ${cleanSym(s.symbol)}`}</button>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── LIVE Bitget Positions Tab ───────────────────────
function LiveTradesTab({ bitget, bybit, userExchange, userMaxTrades }) {
  const isBybit = userExchange === "Bybit";
  const live = isBybit ? bybit : bitget;
  const label = isBybit ? "Bybit" : "Bitget";
  if (!live) return <Loader text={"Connecting to " + label + "..."} />;
  const { positions, equity, available, unrealizedPL } = live;

  return <div>
    <Card style={{ marginBottom: 14, borderColor: `${C.or}25` }} glow={C.or}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 9, color: C.mut, textTransform: "uppercase", fontFamily: C.sans, letterSpacing: "1px" }}>Equity</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: C.or, fontFamily: C.mono }}>${(equity || 0).toFixed(2)}</div>
        </div>
        <div>
          <div style={{ fontSize: 9, color: C.mut, textTransform: "uppercase", fontFamily: C.sans, letterSpacing: "1px" }}>Available</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: C.gn, fontFamily: C.mono }}>${(available || 0).toFixed(2)}</div>
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <div style={{ background: C.inner, borderRadius: 8, padding: "10px 12px", border: `1px solid ${C.bdr}` }}>
          <div style={{ fontSize: 8, color: C.mut, textTransform: "uppercase", fontFamily: C.sans, letterSpacing: "0.5px" }}>Unrealized P&L</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: unrealizedPL >= 0 ? C.gn : C.rd, fontFamily: C.mono }}>
            {unrealizedPL >= 0 ? "+" : ""}${(unrealizedPL || 0).toFixed(2)}
          </div>
        </div>
        <div style={{ background: C.inner, borderRadius: 8, padding: "10px 12px", border: `1px solid ${C.bdr}` }}>
          <div style={{ fontSize: 8, color: C.mut, textTransform: "uppercase", fontFamily: C.sans, letterSpacing: "0.5px" }}>Positions</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: C.tx, fontFamily: C.mono }}>{(positions || []).length}<span style={{ fontSize: 12, color: C.mut }}>/{userMaxTrades || 5}</span></div>
        </div>
      </div>
      <div style={{ marginTop: 8, fontSize: 9, color: C.mut, fontFamily: C.sans, textAlign: "right" }}>Live · refreshes every 30s</div>
    </Card>

    {(!positions || positions.length === 0) ? (
      <Card style={{ textAlign: "center", padding: 50 }}>
        <div style={{ fontSize: 32, marginBottom: 10, opacity: 0.5 }}>📊</div>
        <div style={{ color: C.dim, fontFamily: C.sans }}>No open positions on {label}</div>
      </Card>
    ) : (
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {positions.map((p, i) => {
          const pnl = parseFloat(p.pnlPct) || 0;
          const isLong = p.holdSide === "long";
          return <Card key={i} accent={pnl >= 0 ? C.gn : C.rd}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                <span style={{ fontSize: 15, fontWeight: 800, color: C.tx, fontFamily: C.mono }}>{cleanSym(p.symbol)}</span>
                <Badge type={isLong ? "long" : "short"}>{isLong ? "LONG" : "SHORT"}</Badge>
                <Badge type="info">{p.leverage}x</Badge>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 20, fontWeight: 800, fontFamily: C.mono, color: pnl >= 0 ? C.gn : C.rd }}>{pnl >= 0 ? "+" : ""}{pnl}%</div>
                <div style={{ fontSize: 12, fontWeight: 600, fontFamily: C.mono, color: p.unrealizedPL >= 0 ? C.gn : C.rd }}>
                  {p.unrealizedPL >= 0 ? "+" : ""}${p.unrealizedPL.toFixed(2)}
                </div>
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, background: C.inner, borderRadius: 8, padding: 10, border: `1px solid ${C.bdr}` }}>
              {[
                { l: "Entry", v: fU(p.averageOpenPrice), c: C.tx },
                { l: "Market", v: fU(p.marketPrice), c: pnl >= 0 ? C.gn : C.rd },
                { l: "Margin", v: `$${p.margin.toFixed(2)}`, c: C.yl },
              ].map(({ l, v, c }) => (
                <div key={l}><div style={{ fontSize: 8, color: C.mut, textTransform: "uppercase", fontFamily: C.sans, letterSpacing: "0.5px" }}>{l}</div><div style={{ fontSize: 11, color: c, fontFamily: C.mono, fontWeight: 700 }}>{v}</div></div>
              ))}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginTop: 6, background: C.inner, borderRadius: 8, padding: 10, border: `1px solid ${C.bdr}` }}>
              <div><div style={{ fontSize: 8, color: C.mut, textTransform: "uppercase", fontFamily: C.sans, letterSpacing: "0.5px" }}>Size</div><div style={{ fontSize: 11, color: C.tx, fontFamily: C.mono, fontWeight: 700 }}>{p.total}</div></div>
              <div><div style={{ fontSize: 8, color: C.mut, textTransform: "uppercase", fontFamily: C.sans, letterSpacing: "0.5px" }}>Liq. Price</div><div style={{ fontSize: 11, color: C.rd, fontFamily: C.mono, fontWeight: 700 }}>{fU(p.liquidationPrice)}</div></div>
            </div>
          </Card>;
        })}
      </div>
    )}
  </div>;
}

// ─── Journal Tab (merged with P&L) ───────────────────
function JournalTab({ trades, bitget, bybit, userExchange, currentUser }) {
  /* JOURNAL-FULLHISTORY-V1 — stats+curve from full history, list from recent */
  const cl = trades.filter(t => t.status === "closed" && t.executedBy === currentUser);
  const live = userExchange === "Bybit" ? bybit : bitget;
  const equity = live?.equity || 0;
  const unrealPL = live?.unrealizedPL || 0;

  const [perf, setPerf] = useState(null);
  const [resetting, setResetting] = useState(false); // JOURNAL-RESET v1
  const loadPerf = () => api("/api/my-performance").then(r => { if (r && !r.error) setPerf(r); });
  useEffect(() => { loadPerf(); }, []);
  const doReset = async (undo) => {
    const msg = undo
      ? "Undo the stats reset and count full history again?"
      : "Reset your Journal stats from this point forward?\n\nNo trades are deleted — this only changes what the stats count. You can undo it any time.";
    if (!window.confirm(msg)) return;
    setResetting(true);
    await api("/api/journal-reset", { method: "POST", body: JSON.stringify({ undo: !!undo }) });
    await loadPerf();
    setResetting(false);
  };

  // Full-history equity curve ($) from the server; fall back to recent props if not loaded yet
  const equityCurve = (perf?.equityCurveUsd && perf.equityCurveUsd.length)
    ? perf.equityCurveUsd
    : cl.reduce((acc, t, i) => {
        const prev = acc.length > 0 ? acc[acc.length - 1].eq : 0;
        const d = t.closedAt ? new Date(t.closedAt) : null;
        acc.push({ date: d ? d.toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "#" + (i + 1), ts: t.closedAt || null, eq: Math.round((prev + (t.pnl || 0)) * 100) / 100 });
        return acc;
      }, []);

  if (!cl.length && !perf) return (
    <Card style={{ textAlign: "center", padding: 50 }}>
      <div style={{ fontSize: 32, marginBottom: 10, opacity: 0.5 }}>📝</div>
      <div style={{ color: C.dim, fontFamily: C.sans, fontSize: 14 }}>No closed trades yet</div>
      <div style={{ color: C.mut, fontFamily: C.sans, fontSize: 11, marginTop: 4 }}>Trades will appear here once closed</div>
    </Card>
  );

  // Full-history figures from server (fall back to recent-props while loading)
  const totalTrades = perf?.totalTrades ?? cl.length;
  const winRatePct = perf?.winRate ?? (cl.length ? (cl.filter(t => t.outcome === "win").length / cl.length) * 100 : 0);
  const tot = perf?.totalPnl ?? cl.reduce((s, t) => s + (t.pnl || 0), 0);
  const avgWin = perf?.avgWin ?? (cl.filter(t => (t.pnl || 0) > 0).reduce((s, t) => s + t.pnl, 0) / (cl.filter(t => (t.pnl || 0) > 0).length || 1));
  const avgLoss = perf?.avgLoss ?? (() => { const l = cl.filter(t => (t.pnl || 0) < 0); return l.length ? l.reduce((s, t) => s + t.pnl, 0) / l.length : 0; })();

  return <div>
    {/* Equity Curve */}
    {equityCurve.length >= 2 && ( /* FE-JOURNAL-LIST v1 + EQ-CURVE v2 */
      <Card style={{ marginBottom: 14 }} glow={tot >= 0 ? C.gn : C.rd}>
        {(() => {
          const _c = tot >= 0 ? C.gn : C.rd;
          const _eqs = equityCurve.map(p => p.eq);
          const _now = _eqs[_eqs.length - 1] || 0;
          const _hi = Math.max(..._eqs), _lo = Math.min(..._eqs);
          const _pad = Math.max(1, (_hi - _lo) * 0.15);
          const _endDot = (p) => (p.index === equityCurve.length - 1)
            ? <circle key="eqEnd" cx={p.cx} cy={p.cy} r={4.5} fill={_c} stroke={C.card} strokeWidth={2} />
            : <g key={"d" + p.index} />;
          return <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <Stitle icon="📈" color={_c}>Equity Curve</Stitle>
              <div style={{ display: "flex", gap: 14, textAlign: "right" }}>
                <div><div style={{ fontSize: 8, color: C.mut, fontFamily: C.mono, letterSpacing: 1 }}>NOW</div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: _c, fontFamily: C.mono }}>{(_now >= 0 ? "+$" : "-$") + Math.abs(_now).toFixed(2)}</div></div>
                <div><div style={{ fontSize: 8, color: C.mut, fontFamily: C.mono, letterSpacing: 1 }}>PEAK</div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: C.gn, fontFamily: C.mono, marginTop: 3 }}>{(_hi >= 0 ? "+$" : "-$") + Math.abs(_hi).toFixed(2)}</div></div>
                <div><div style={{ fontSize: 8, color: C.mut, fontFamily: C.mono, letterSpacing: 1 }}>TROUGH</div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: C.rd, fontFamily: C.mono, marginTop: 3 }}>{(_lo >= 0 ? "+$" : "-$") + Math.abs(_lo).toFixed(2)}</div></div>
              </div>
            </div>
            <div style={{ height: 190, marginTop: 8 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={equityCurve} margin={{ top: 8, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="eqFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={_c} stopOpacity={0.32} />
                      <stop offset="55%" stopColor={_c} stopOpacity={0.10} />
                      <stop offset="100%" stopColor={_c} stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="eqStroke" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor={_c} stopOpacity={0.55} />
                      <stop offset="100%" stopColor={_c} stopOpacity={1} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke={C.bdr} strokeOpacity={0.35} strokeDasharray="2 6" vertical={false} />
                  <XAxis dataKey="date" tick={{ fill: C.mut, fontSize: 9, fontFamily: C.mono }} tickLine={false} axisLine={{ stroke: C.bdr }} minTickGap={34} interval="preserveStartEnd" />
                  <YAxis domain={[_lo - _pad, _hi + _pad]} tick={{ fill: C.mut, fontSize: 9, fontFamily: C.mono }} tickLine={false} axisLine={false} width={52}
                    tickFormatter={(v) => (Math.abs(v) >= 1000 ? "$" + (v / 1000).toFixed(1) + "k" : "$" + v.toFixed(0))} />
                  <ReferenceLine y={0} stroke={C.mut} strokeOpacity={0.55} strokeDasharray="4 4" />
                  <Area type="monotone" dataKey="eq" stroke="url(#eqStroke)" strokeWidth={2.5} fill="url(#eqFill)"
                    dot={_endDot} activeDot={{ r: 5, fill: _c, stroke: C.card, strokeWidth: 2 }} isAnimationActive={true} animationDuration={700} />
                  <Tooltip cursor={{ stroke: C.mut, strokeOpacity: 0.4, strokeDasharray: "3 3" }}
                    contentStyle={{ background: C.card, border: `1px solid ${C.bdr}`, borderRadius: 10, fontFamily: C.mono, fontSize: 11, boxShadow: "0 6px 24px rgba(0,0,0,0.45)" }}
                    formatter={(v) => [(v >= 0 ? "+$" : "-$") + Math.abs(v).toFixed(2), "Equity"]}
                    labelFormatter={(l, p) => (p && p[0] && p[0].payload && p[0].payload.date) ? p[0].payload.date : `Trade #${l}`} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </>;
        })()}
      </Card>
    )}

    {/* Stats row */}
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8, marginBottom: 14 }}>
      <Stat icon="📊" label="Trades" value={totalTrades} />
      <Stat icon="🎯" label="Win %" value={`${winRatePct.toFixed(0)}%`} color={winRatePct >= 55 ? C.gn : winRatePct >= 45 ? C.yl : C.rd} />
      <Stat icon="💰" label="P&L" value={`${tot >= 0 ? "+" : ""}${fU(Math.abs(tot))}`} color={tot >= 0 ? C.gn : C.rd} />
      <Stat icon="📈" label="Avg Win" value={fU(Math.abs(avgWin))} sub={`Loss: ${fU(Math.abs(avgLoss))}`} color={C.gn} />
    </div>

    {/* JOURNAL-RESET v1 */}
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, padding: "0 2px" }}>
      <div style={{ fontSize: 10, color: C.mut, fontFamily: C.mono }}>
        {perf?.statsResetAt ? "Stats since " + new Date(perf.statsResetAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "Stats: full history"}
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        {perf?.statsResetAt != null && <button onClick={() => doReset(true)} disabled={resetting} style={{ background: "none", border: "1px solid " + C.bdr, color: C.dim, borderRadius: 6, padding: "4px 10px", fontSize: 10, fontFamily: C.mono, cursor: "pointer" }}>Undo reset</button>}
        <button onClick={() => doReset(false)} disabled={resetting} style={{ background: C.inner, border: "1px solid " + C.bdr, color: C.dim, borderRadius: 6, padding: "4px 10px", fontSize: 10, fontFamily: C.mono, cursor: "pointer" }}>{resetting ? "..." : "Reset stats"}</button>
      </div>
    </div>

    {/* Live equity if available */}
    {equity > 0 && (
      <Card style={{ marginBottom: 14, borderColor: `${C.or}25` }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 9, color: C.mut, textTransform: "uppercase", fontFamily: C.sans }}>Live Equity</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: C.or, fontFamily: C.mono }}>${equity.toFixed(2)}</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 9, color: C.mut, textTransform: "uppercase", fontFamily: C.sans }}>Unrealized</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: unrealPL >= 0 ? C.gn : C.rd, fontFamily: C.mono }}>{unrealPL >= 0 ? "+" : ""}${unrealPL.toFixed(2)}</div>
          </div>
        </div>
      </Card>
    )}

    {/* Trade list — capped to most recent 100 for performance (stats & curve use ALL) */}
    {cl.length > 100 && (
      <div style={{ fontSize: 10, color: C.mut, fontFamily: C.mono, textAlign: "center", marginBottom: 8 }}>
        Showing 100 most recent of {cl.length.toLocaleString()} closed trades
      </div>
    )}
    {/* FE-JOURNAL-LIST v1: server sorts newest-first; the old .reverse() flipped it back */}
    {[...cl].sort((a, b) => ((b.closedAt || b.openedAt || 0) - (a.closedAt || a.openedAt || 0))).slice(0, 100).map((t, i) => (
      <Card key={i} style={{ padding: 12, marginBottom: 6, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 4 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 5, flexWrap: "wrap" }}>
          <Badge type={t.outcome === "win" ? "win" : t.outcome === "loss" ? "loss" : "neutral"}>{t.outcome || "closed"}</Badge>
          <span style={{ fontWeight: 700, color: C.tx, fontSize: 12, fontFamily: C.mono }}>{cleanSym(t.symbol)}</span>
          <Badge type={t.direction === "LONG" ? "long" : "short"}>{t.direction}</Badge>
        </div>
        <span style={{ fontSize: 13, fontWeight: 700, fontFamily: C.mono, color: (t.pnl || 0) >= 0 ? C.gn : C.rd }}>{(t.pnl || 0) >= 0 ? "+" : ""}{fU(Math.abs(t.pnl || 0))}</span>
      </Card>
    ))}
  </div>;
}

// ─── Settings Tab (merged with Risk calculator) ──────
function RefusalTicker() { // REFUSAL-TICKER v1
  const [d, setD] = useState(null);
  useEffect(() => { let alive = true; const load = () => api("/api/refusal-ticker").then(r => { if (alive && r && r.ok) setD(r); }).catch(() => {}); load(); const t = setInterval(load, 300000); return () => { alive = false; clearInterval(t); }; }, []);
  if (!d || !d.refused) return null;
  const lost = d.sumR < 0;
  return (<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10, padding: "10px 12px", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8 }}>
    <div style={{ fontSize: 11, fontWeight: 700, color: C.or, fontFamily: C.display, textTransform: "uppercase", letterSpacing: "0.08em" }}>Refused this week</div>
    <div style={{ fontSize: 11, color: C.mut, fontFamily: C.mono, textAlign: "right" }}>{d.refused} setups declined · they went on to {lost ? "lose" : "make"} {Math.abs(d.sumR)}R · {lost ? "declined exposure" : "missed"} ≈ ${Math.abs(d.declinedUSD).toLocaleString()} <span style={{ opacity: 0.6 }}>(at $150/R, graded on paper)</span></div>
  </div>);
}
function SettingsTab({ config, onSave, onReset, onLogout, currentUser, userRole, balance }) {
  if (userRole === "viewer") return (<div style={{ padding: 24, maxWidth: 560 }}>{/* OBSERVER-SEAT v2 (on v1) */}
    <h3 style={{ margin: "0 0 8px" }}>👁 Observer seat</h3>
    <p style={{ opacity: 0.75, fontSize: 13, lineHeight: 1.6 }}>This account is read-only — enforced by the server, not this page. You can watch every verdict, ledger, and card; you cannot change anything, trade anything, or enter keys.</p>
    <button onClick={onLogout} style={{ marginTop: 12, padding: "8px 16px", cursor: "pointer" }}>Logout</button>
  </div>);
  const [bal, setBal] = useState(config.accountBalance || 1000);
  const [sl, setSl] = useState(config.scanPairsLimit || 50);
  const [users, setUsers] = useState([]);
  const [newUser, setNewUser] = useState(""); const [newPass, setNewPass] = useState(""); const [userMsg, setUserMsg] = useState("");
  const [apiKey, setApiKey] = useState(""); const [secretKey, setSecretKey] = useState(""); const [passphrase, setPassphrase] = useState(""); const [keyMsg, setKeyMsg] = useState("");
  const [exchange, setExchange] = useState("Bitget"); const [bybitMode, setBybitMode] = useState("demo");
  const [bybitApiKey, setBybitApiKey] = useState(""); const [bybitSecretKey, setBybitSecretKey] = useState(""); const [bybitKeyMsg, setBybitKeyMsg] = useState("");
  const [mySettings, setMySettings] = useState(null); const [autoLevel, setAutoLevel] = useState(0); const [tgChatId, setTgChatId] = useState(""); const [riskPct, setRiskPct] = useState("1"); const [myBalance, setMyBalance] = useState("1000"); const [myLeverage, setMyLeverage] = useState(5); const [maxTrades, setMaxTrades] = useState("5"); // PATCHED: SettingsUI v1
  // Risk calculator
  const [rp, setRp] = useState(1); const [ent, setEnt] = useState(""); const [slCalc, setSlCalc] = useState(""); const [lev, setLev] = useState(3);
  const ra = balance * (rp / 100), sd = ent && slCalc ? Math.abs(parseFloat(ent) - parseFloat(slCalc)) : 0;
  const ps = sd > 0 ? ra / sd : 0, nt = ps * (parseFloat(ent) || 0), el = balance > 0 ? nt / balance : 0;

  const iS = { width: "100%", padding: "10px 12px", background: C.inner, border: `1px solid ${C.bdr}`, borderRadius: 8, color: C.tx, fontFamily: C.mono, fontSize: 12, outline: "none", boxSizing: "border-box", transition: "border-color 0.2s" };

  useEffect(() => {
    api("/api/my-settings").then(r => { if (r.ok && r.data) { setMySettings(r.data); setAutoLevel(r.data.autoLevel || 0); setTgChatId(r.data.telegramChatId || ""); setRiskPct(String(r.data.riskPct || 1.5)); setMyBalance(String(r.data.balance || 100)); setMyLeverage(r.data.maxLeverage || 5); setExchange(r.data.exchange || "Bitget"); setBybitMode(r.data.bybitMode || "demo"); setMaxTrades(String(r.data.maxOpenTrades || ((r.data.bybitMode === "live" || r.data.exchange === "Bitget") ? 5 : 100))); } });
    if (userRole === "admin") api("/api/users").then(r => { if (r.ok) setUsers(r.data || []); });
  }, [userRole]);

  const saveMySettings = async () => { const parsedRisk = parseFloat(riskPct); const parsedBal = parseFloat(myBalance); const parsedMax = parseInt(maxTrades); const r = await api("/api/my-settings", { method: "POST", body: JSON.stringify({ autoLevel, telegramChatId: tgChatId || null, riskPct: isNaN(parsedRisk) ? 1.5 : Math.min(10, Math.max(0.1, parsedRisk)), balance: isNaN(parsedBal) ? 100 : Math.max(1, parsedBal), maxLeverage: myLeverage, maxOpenTrades: isNaN(parsedMax) ? 5 : Math.min(200, Math.max(1, parsedMax)), bybitMode }) }); if (r.ok) setMySettings(r.data); setKeyMsg(r.ok ? "✓ Saved" : "✗ Failed"); setTimeout(() => setKeyMsg(""), 3000); };
  const saveKeys = async () => { if (!apiKey || !secretKey || !passphrase) { setKeyMsg("✗ All fields required"); return; } const r = await api("/api/my-keys", { method: "POST", body: JSON.stringify({ apiKey, secretKey, passphrase }) }); setKeyMsg(r.ok ? "✓ Keys encrypted" : `✗ ${r.error}`); setApiKey(""); setSecretKey(""); setPassphrase(""); if (r.ok) api("/api/my-settings").then(r => { if (r.ok) setMySettings(r.data); }); setTimeout(() => setKeyMsg(""), 3000); };
  const removeKeys = async () => { await api("/api/my-keys", { method: "POST", body: JSON.stringify({ action: "remove" }) }); api("/api/my-settings").then(r => { if (r.ok) setMySettings(r.data); }); setKeyMsg("Removed"); setTimeout(() => setKeyMsg(""), 3000); };
  const validateKeys = async () => { setKeyMsg("Validating..."); const r = await api("/api/my-keys/validate", { method: "POST" }); setKeyMsg(r.ok && r.data?.valid ? "✓ Connected to Bitget" : `✗ ${r.data?.error || "Failed"}`); setTimeout(() => setKeyMsg(""), 5000); };
  const saveExchange = async () => { const r = await api("/api/my-settings", { method: "POST", body: JSON.stringify({ exchange, bybitMode }) }); if (r.ok) setMySettings(r.data); setKeyMsg(r.ok ? "✓ Exchange saved" : "✗ Failed"); setTimeout(() => setKeyMsg(""), 3000); };
  const saveBybitKeys = async () => { if (!bybitApiKey || !bybitSecretKey) { setBybitKeyMsg("✗ All fields required"); return; } const r = await api("/api/my-bybit-keys", { method: "POST", body: JSON.stringify({ apiKey: bybitApiKey, secretKey: bybitSecretKey, mode: bybitMode }) }); setBybitKeyMsg(r.ok ? "✓ Bybit keys encrypted" : `✗ ${r.error}`); setBybitApiKey(""); setBybitSecretKey(""); if (r.ok) api("/api/my-settings").then(r => { if (r.ok) setMySettings(r.data); }); setTimeout(() => setBybitKeyMsg(""), 3000); };
  const removeBybitKeys = async () => { await api("/api/my-bybit-keys", { method: "POST", body: JSON.stringify({ action: "remove" }) }); api("/api/my-settings").then(r => { if (r.ok) setMySettings(r.data); }); setBybitKeyMsg("Removed"); setTimeout(() => setBybitKeyMsg(""), 3000); };
  const validateBybitKeys = async () => { setBybitKeyMsg("Validating..."); const r = await api("/api/my-bybit-keys/validate", { method: "POST" }); setBybitKeyMsg(r.ok && r.data?.valid ? `✓ Connected (${r.data.mode}, ${r.data.balance?.toFixed(2)})` : `✗ ${r.data?.error || "Failed"}`); setTimeout(() => setBybitKeyMsg(""), 5000); };
  const addUser = async () => { if (!newUser || !newPass) return; const r = await api("/api/users", { method: "POST", body: JSON.stringify({ action: "add", username: newUser, password: newPass, role: "viewer" }) }); setUserMsg(r.ok ? `✓ ${newUser} added` : `✗ ${r.error}`); setNewUser(""); setNewPass(""); if (r.ok) api("/api/users").then(r => { if (r.ok) setUsers(r.data || []); }); };
  const removeUser = async (u) => { if (u === currentUser) return; await api("/api/users", { method: "POST", body: JSON.stringify({ action: "remove", username: u }) }); api("/api/users").then(r => { if (r.ok) setUsers(r.data || []); }); };

  const keysOk = mySettings?.keysConfigured;
  const capTier = mySettings?.capTier || 1;

  return <div>
    {/* User card */}
    <Card style={{ marginBottom: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div><div style={{ fontSize: 10, color: C.mut, textTransform: "uppercase", fontFamily: C.sans, letterSpacing: "1px" }}>Signed in as</div><div style={{ fontSize: 16, fontWeight: 800, color: C.or, fontFamily: C.mono }}>{currentUser}</div></div>
        <button onClick={onLogout} style={{ padding: "8px 16px", background: `${C.rd}10`, border: `1px solid ${C.rd}35`, color: C.rd, borderRadius: 8, cursor: "pointer", fontFamily: C.mono, fontSize: 10, fontWeight: 700, textTransform: "uppercase", transition: "all 0.2s" }}>Sign Out</button>
      </div>
    </Card>

    {/* Exchange Selection — v1 patch */}
    <Card style={{ marginBottom: 14 }}>
      <Stitle icon="🔁">Exchange</Stitle>
      <div style={{ fontSize: 11, color: C.mut, fontFamily: C.sans, marginBottom: 10 }}>Pick which exchange your trades execute on. The platform scans coins from both regardless.</div>
      <div style={{ display: "grid", gap: 8, marginBottom: 12 }}>
        <select value={exchange} onChange={e => setExchange(e.target.value)} style={iS}>
          <option value="Bitget">Bitget</option>
          <option value="Bybit">Bybit</option>
        </select>
        {exchange === "Bybit" && (
          <select value={bybitMode} onChange={e => setBybitMode(e.target.value)} style={iS}>
            <option value="demo">Demo (virtual funds, api-demo.bybit.com)</option>
            <option value="live">Live (real funds, api.bybit.com)</option>
          </select>
        )}
      </div>
      <button onClick={saveExchange} style={{ width: "100%", padding: 11, background: `${C.bl}10`, border: `1px solid ${C.bl}`, color: C.bl, borderRadius: 8, cursor: "pointer", fontFamily: C.mono, fontSize: 10, fontWeight: 700, textTransform: "uppercase" }}>Save Exchange Choice</button>
    </Card>

    {/* Bitget API Keys (visible when exchange=Bitget) */}
    {exchange === "Bitget" && (
    <Card style={{ marginBottom: 14 }}>
      <Stitle icon="🔑">Bitget API Keys</Stitle>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <div style={{ width: 8, height: 8, borderRadius: "50%", background: keysOk ? C.gn : C.rd, boxShadow: `0 0 8px ${keysOk ? C.gn : C.rd}40` }} />
        <span style={{ fontSize: 11, color: keysOk ? C.gn : C.rd, fontFamily: C.sans }}>{keysOk ? "Keys configured" : "No keys — add to enable trading"}</span>
      </div>
      {!keysOk ? (
        <div>
          <div style={{ display: "grid", gap: 8, marginBottom: 12 }}>
            <input type="text" value={apiKey} onChange={e => setApiKey(e.target.value)} placeholder="API Key" style={iS} />
            <input type="password" value={secretKey} onChange={e => setSecretKey(e.target.value)} placeholder="Secret Key" style={iS} />
            <input type="password" value={passphrase} onChange={e => setPassphrase(e.target.value)} placeholder="Passphrase" style={iS} />
          </div>
          <button onClick={saveKeys} style={{ width: "100%", padding: 11, background: `${C.gn}10`, border: `1px solid ${C.gn}`, color: C.gn, borderRadius: 8, cursor: "pointer", fontFamily: C.mono, fontSize: 10, fontWeight: 700, textTransform: "uppercase" }}>Save & Encrypt</button>
        </div>
      ) : (
        <div style={{ display: "flex", gap: 6 }}>
          <button onClick={validateKeys} style={{ flex: 1, padding: 10, background: `${C.bl}10`, border: `1px solid ${C.bl}`, color: C.bl, borderRadius: 8, cursor: "pointer", fontFamily: C.mono, fontSize: 10, fontWeight: 700 }}>Validate</button>
          <button onClick={removeKeys} style={{ padding: "10px 16px", background: `${C.rd}10`, border: `1px solid ${C.rd}35`, color: C.rd, borderRadius: 8, cursor: "pointer", fontFamily: C.mono, fontSize: 10, fontWeight: 700 }}>Remove</button>
        </div>
      )}
      {keyMsg && <div style={{ marginTop: 8, fontSize: 11, color: keyMsg.startsWith("✓") ? C.gn : keyMsg.startsWith("✗") ? C.rd : C.dim, fontFamily: C.sans }}>{keyMsg}</div>}
    </Card>
    )}

    {/* Bybit API Keys (visible when exchange=Bybit) */}
    {exchange === "Bybit" && (() => {
      const bybitKeysOk = mySettings?.bybitKeysConfigured;
      return (
      <Card style={{ marginBottom: 14 }}>
        <Stitle icon="🔑">Bybit API Keys <span style={{ fontSize: 10, color: C.mut }}>({bybitMode})</span></Stitle>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: bybitKeysOk ? C.gn : C.rd, boxShadow: `0 0 8px ${bybitKeysOk ? C.gn : C.rd}40` }} />
          <span style={{ fontSize: 11, color: bybitKeysOk ? C.gn : C.rd, fontFamily: C.sans }}>{bybitKeysOk ? `Keys configured (${bybitMode})` : "No keys — add to enable trading"}</span>
        </div>
        {!bybitKeysOk ? (
          <div>
            <div style={{ display: "grid", gap: 8, marginBottom: 12 }}>
              <input type="text" value={bybitApiKey} onChange={e => setBybitApiKey(e.target.value)} placeholder="Bybit API Key" style={iS} />
              <input type="password" value={bybitSecretKey} onChange={e => setBybitSecretKey(e.target.value)} placeholder="Bybit API Secret" style={iS} />
            </div>
            <button onClick={saveBybitKeys} style={{ width: "100%", padding: 11, background: `${C.gn}10`, border: `1px solid ${C.gn}`, color: C.gn, borderRadius: 8, cursor: "pointer", fontFamily: C.mono, fontSize: 10, fontWeight: 700, textTransform: "uppercase" }}>Save & Encrypt</button>
          </div>
        ) : (
          <div style={{ display: "flex", gap: 6 }}>
            <button onClick={validateBybitKeys} style={{ flex: 1, padding: 10, background: `${C.bl}10`, border: `1px solid ${C.bl}`, color: C.bl, borderRadius: 8, cursor: "pointer", fontFamily: C.mono, fontSize: 10, fontWeight: 700 }}>Validate</button>
            <button onClick={removeBybitKeys} style={{ padding: "10px 16px", background: `${C.rd}10`, border: `1px solid ${C.rd}35`, color: C.rd, borderRadius: 8, cursor: "pointer", fontFamily: C.mono, fontSize: 10, fontWeight: 700 }}>Remove</button>
          </div>
        )}
        {bybitKeyMsg && <div style={{ marginTop: 8, fontSize: 11, color: bybitKeyMsg.startsWith("✓") ? C.gn : bybitKeyMsg.startsWith("✗") ? C.rd : C.dim, fontFamily: C.sans }}>{bybitKeyMsg}</div>}
      </Card>
      );
    })()}

    {/* Automation */}
    <Card style={{ marginBottom: 14 }}>
      <Stitle icon="🤖">Automation Level</Stitle>
      <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 12 }}>
        {[{ v: 0, l: "Manual", d: "View signals only", icon: "👁" }, { v: 1, l: "Semi-Auto", d: "Click Execute on dashboard", icon: "👆" }, { v: 2, l: "Telegram Approval", d: "Tap Confirm in Telegram", icon: "📱" }, { v: 3, l: "Full Auto", d: "Automatic execution", icon: "🤖" }].map(o => (
          <button key={o.v} onClick={() => setAutoLevel(o.v)} style={{ padding: "11px 14px", background: autoLevel === o.v ? `${C.or}10` : C.inner, border: `1px solid ${autoLevel === o.v ? C.or : C.bdr}`, borderRadius: 10, cursor: "pointer", textAlign: "left", display: "flex", gap: 12, alignItems: "center", transition: "all 0.2s" }}>
            <span style={{ fontSize: 20 }}>{o.icon}</span>
            <div><div style={{ fontSize: 12, fontWeight: 700, color: autoLevel === o.v ? C.or : C.tx, fontFamily: C.sans }}>{o.l}</div><div style={{ fontSize: 9, color: C.dim, fontFamily: C.sans, marginTop: 1 }}>{o.d}</div></div>
          </button>
        ))}
      </div>
      {autoLevel >= 2 && <div style={{ marginBottom: 10 }}><label style={{ fontSize: 9, color: C.mut, textTransform: "uppercase", display: "block", marginBottom: 4, fontFamily: C.sans, letterSpacing: "1px" }}>Telegram Chat ID</label><input type="text" value={tgChatId} onChange={e => setTgChatId(e.target.value)} placeholder="1302012335" style={iS} /></div>}
    </Card>

    {/* Trading Settings */}
    <Card style={{ marginBottom: 14 }}>
      <Stitle icon="⚙">Trading Settings</Stitle>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
        <div><label style={{ fontSize: 9, color: C.mut, textTransform: "uppercase", display: "block", marginBottom: 4, fontFamily: C.sans, letterSpacing: "1px" }}>Balance ($)</label><input type="number" value={myBalance} onChange={e => setMyBalance(e.target.value)} onBlur={e => { if (e.target.value === "" || isNaN(parseFloat(e.target.value))) setMyBalance("100"); }} style={iS} /></div>
        <div><label style={{ fontSize: 9, color: C.mut, textTransform: "uppercase", display: "block", marginBottom: 4, fontFamily: C.sans, letterSpacing: "1px" }}>Risk (%)</label><input type="number" value={riskPct} onChange={e => setRiskPct(e.target.value)} onBlur={e => { const v = parseFloat(e.target.value); if (isNaN(v)) setRiskPct("1.5"); else setRiskPct(String(Math.min(10, Math.max(0.1, v)))); }} step={0.5} style={iS} /></div>
      </div>
      <div style={{ marginBottom: 12 }}>
        <label style={{ fontSize: 9, color: C.mut, textTransform: "uppercase", display: "block", marginBottom: 4, fontFamily: C.sans, letterSpacing: "1px" }}>Max Leverage</label>
        <div style={{ display: "flex", gap: 4 }}>{[1, 2, 3, 5, 10].map(l => <button key={l} onClick={() => setMyLeverage(l)} style={{ flex: 1, padding: 9, background: myLeverage === l ? `${C.or}15` : C.inner, border: `1px solid ${myLeverage === l ? C.or : C.bdr}`, color: myLeverage === l ? C.or : C.mut, borderRadius: 8, cursor: "pointer", fontFamily: C.mono, fontSize: 12, fontWeight: 700, transition: "all 0.2s" }}>{l}x</button>)}</div>
      </div>
      <div style={{ marginBottom: 12 }}>
        <label style={{ fontSize: 9, color: C.mut, textTransform: "uppercase", display: "block", marginBottom: 4, fontFamily: C.sans, letterSpacing: "1px" }}>Max Open Trades</label>
        <input type="number" value={maxTrades} onChange={e => setMaxTrades(e.target.value)} onBlur={e => { const v = parseInt(e.target.value); if (isNaN(v)) setMaxTrades((bybitMode === "demo" && exchange === "Bybit") ? "100" : "5"); else setMaxTrades(String(Math.min(200, Math.max(1, v)))); }} style={iS} />
        <div style={{ fontSize: 9, color: C.dim, fontFamily: C.sans, marginTop: 4 }}>{exchange === "Bybit" && bybitMode === "demo" ? "💡 Demo: 100 recommended for ML training data" : "💡 Live: 5 recommended for capital discipline"}</div>
      </div>
      
      <button onClick={saveMySettings} style={{ width: "100%", padding: 11, background: `${C.or}10`, border: `1px solid ${C.or}`, color: C.or, borderRadius: 8, cursor: "pointer", fontFamily: C.mono, fontSize: 10, fontWeight: 700, textTransform: "uppercase" }}>Save All Settings</button>
    </Card>

    {/* Position Calculator */}
    <Card style={{ marginBottom: 14 }}>
      <Stitle icon="🧮">Position Calculator</Stitle>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
        <div><label style={{ fontSize: 9, color: C.mut, textTransform: "uppercase", display: "block", marginBottom: 4, fontFamily: C.sans }}>Entry</label><input type="number" value={ent} onChange={e => setEnt(e.target.value)} placeholder="70585" style={iS} /></div>
        <div><label style={{ fontSize: 9, color: C.mut, textTransform: "uppercase", display: "block", marginBottom: 4, fontFamily: C.sans }}>Stop Loss</label><input type="number" value={slCalc} onChange={e => setSlCalc(e.target.value)} placeholder="68000" style={iS} /></div>
        <div><label style={{ fontSize: 9, color: C.mut, textTransform: "uppercase", display: "block", marginBottom: 4, fontFamily: C.sans }}>Risk %</label><input type="number" value={rp} onChange={e => setRp(Math.min(10, Math.max(0.5, parseFloat(e.target.value) || 0)))} step={0.5} style={iS} /></div>
        <div><label style={{ fontSize: 9, color: C.mut, textTransform: "uppercase", display: "block", marginBottom: 4, fontFamily: C.sans }}>Leverage</label>
          <div style={{ display: "flex", gap: 3 }}>{[1, 3, 5, 10].map(l => <button key={l} onClick={() => setLev(l)} style={{ flex: 1, padding: 7, background: lev === l ? `${C.or}15` : C.inner, border: `1px solid ${lev === l ? C.or : C.bdr}`, color: lev === l ? C.or : C.mut, borderRadius: 6, cursor: "pointer", fontFamily: C.mono, fontSize: 11, fontWeight: 700 }}>{l}x</button>)}</div>
        </div>
      </div>
      {sd > 0 && <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 6 }}>
        <div style={{ background: C.inner, borderRadius: 6, padding: 8, border: `1px solid ${C.bdr}` }}><div style={{ fontSize: 8, color: C.mut, textTransform: "uppercase", fontFamily: C.sans }}>Risk $</div><div style={{ fontSize: 12, color: C.yl, fontFamily: C.mono, fontWeight: 700 }}>${ra.toFixed(2)}</div></div>
        <div style={{ background: C.inner, borderRadius: 6, padding: 8, border: `1px solid ${C.bdr}` }}><div style={{ fontSize: 8, color: C.mut, textTransform: "uppercase", fontFamily: C.sans }}>SL Dist</div><div style={{ fontSize: 12, color: C.rd, fontFamily: C.mono, fontWeight: 700 }}>${sd.toFixed(2)}</div></div>
        <div style={{ background: C.inner, borderRadius: 6, padding: 8, border: `1px solid ${C.bdr}` }}><div style={{ fontSize: 8, color: C.mut, textTransform: "uppercase", fontFamily: C.sans }}>Size</div><div style={{ fontSize: 12, color: C.tx, fontFamily: C.mono, fontWeight: 700 }}>{ps.toFixed(4)}</div></div>
        <div style={{ background: C.inner, borderRadius: 6, padding: 8, border: `1px solid ${C.bdr}` }}><div style={{ fontSize: 8, color: C.mut, textTransform: "uppercase", fontFamily: C.sans }}>Eff Lev</div><div style={{ fontSize: 12, color: el > 10 ? C.rd : C.gn, fontFamily: C.mono, fontWeight: 700 }}>{el.toFixed(1)}x</div></div>
      </div>}
    </Card>

    {/* Admin: Users */}
    {userRole === "admin" && <Card style={{ marginBottom: 14 }}>
      <Stitle icon="👥">Users</Stitle>
      {users.map(u => <div key={u.username} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: `1px solid ${C.inner}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}><span style={{ fontSize: 12, fontWeight: 600, color: C.tx, fontFamily: C.mono }}>{u.username}</span><Badge type={u.role === "admin" ? "active" : "info"}>{u.role}</Badge></div>
        {u.username !== currentUser && <button onClick={() => removeUser(u.username)} style={{ padding: "4px 10px", background: `${C.rd}08`, border: `1px solid ${C.rd}25`, color: C.rd, borderRadius: 6, cursor: "pointer", fontSize: 9, fontFamily: C.mono, fontWeight: 700 }}>Remove</button>}
      </div>)}
      <div style={{ marginTop: 14, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <input type="text" value={newUser} onChange={e => setNewUser(e.target.value)} placeholder="Username" style={iS} />
        <input type="password" value={newPass} onChange={e => setNewPass(e.target.value)} placeholder="Password" style={iS} />
      </div>
      <button onClick={addUser} style={{ width: "100%", marginTop: 8, padding: 10, background: `${C.gn}10`, border: `1px solid ${C.gn}`, color: C.gn, borderRadius: 8, cursor: "pointer", fontFamily: C.mono, fontSize: 10, fontWeight: 700, textTransform: "uppercase" }}>Add User</button>
      {userMsg && <div style={{ marginTop: 8, fontSize: 11, color: userMsg.startsWith("✓") ? C.gn : C.rd, fontFamily: C.sans }}>{userMsg}</div>}
    </Card>}

    {/* Admin: Scanner config */}
    {userRole === "admin" && <Card style={{ marginBottom: 14 }}>
      <Stitle icon="📡">Scanner Config</Stitle>
      <div style={{ display: "grid", gap: 10, marginBottom: 14 }}>
        <div><label style={{ fontSize: 9, color: C.mut, textTransform: "uppercase", display: "block", marginBottom: 4, fontFamily: C.sans }}>Scanner Balance ($)</label><input type="number" value={bal} onChange={e => setBal(parseFloat(e.target.value) || 1000)} style={iS} /></div>
        <div><label style={{ fontSize: 9, color: C.mut, textTransform: "uppercase", display: "block", marginBottom: 4, fontFamily: C.sans }}>Scan Limit</label><input type="number" value={sl} onChange={e => setSl(parseInt(e.target.value) || 50)} style={iS} /></div>
      </div>
      <div style={{ display: "flex", gap: 6 }}>
        <button onClick={() => onSave({ accountBalance: bal, scanPairsLimit: sl })} style={{ flex: 1, padding: 10, background: `${C.or}10`, border: `1px solid ${C.or}`, color: C.or, borderRadius: 8, cursor: "pointer", fontFamily: C.mono, fontSize: 10, fontWeight: 700, textTransform: "uppercase" }}>Save</button>
        <button onClick={onReset} style={{ padding: "10px 16px", background: `${C.rd}10`, border: `1px solid ${C.rd}`, color: C.rd, borderRadius: 8, cursor: "pointer", fontFamily: C.mono, fontSize: 10, fontWeight: 700, textTransform: "uppercase" }}>Reset</button>
      </div>
    </Card>}
  </div>;
}

// ─── Bottom Nav (5 tabs) ─────────────────────────────
// ─── Admin Performance Tab (admin only) ──────────────
// ─── Bitscanner AI — Verdicts Tab ────────────────────
function AITab({ fearGreed, scan }) { // FRONTEND-BATCH v1
  const [verdicts, setVerdicts] = useState(null);
  const [apprTrades, setApprTrades] = useState([]); // FE-APPROVALS v1
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchVerdicts = async () => {
      const r = await api("/api/claude-verdicts");
      if (r.ok) setVerdicts(r.data || []);
      // FE-APPROVALS v1: durable approval showcase from the trades store,
      // deduped to distinct signals (3 accounts execute the same approval).
      try {
        const tr = await api("/api/trades?limit=300");
        if (tr.ok) {
          const appr = (tr.data || []).filter((t) => t.claudeApproved === true);
          const seen = new Map();
          appr.forEach((t) => {
            const k = t.symbol + "|" + t.direction + "|" + Math.round((t.openedAt || 0) / 3600000);
            const e = seen.get(k);
            if (!e) seen.set(k, { ...t, _accounts: 1 });
            else {
              e._accounts++;
              if (t.status === "closed" && e.status !== "closed") Object.assign(e, { status: t.status, outcome: t.outcome, closePrice: t.closePrice });
            }
          });
          setApprTrades([...seen.values()].sort((a, b) => (b.openedAt || 0) - (a.openedAt || 0)));
        }
      } catch (e) {}
      setLoading(false);
    };
    fetchVerdicts();
    const t = setInterval(fetchVerdicts, 60000);
    return () => clearInterval(t);
  }, []);

  const fgVal = fearGreed?.value ?? fearGreed?.fearGreed ?? null;
  const fgLabel = fearGreed?.label ?? fearGreed?.classification ?? "";

  return (
    <div>
      {/* MARKET-REGIME-HEADER-V2 */}
      <Card style={{ marginBottom: 14, position: "relative", overflow: "hidden" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 11, color: C.mut, fontFamily: C.sans, textTransform: "uppercase", letterSpacing: "0.1em" }}>Market regime</div>
            <div style={{ fontSize: 26, fontWeight: 700, color: fgVal == null ? C.dim : fgVal < 25 ? C.rd : fgVal < 45 ? "#ff8800" : fgVal < 55 ? C.yl : C.gn, fontFamily: C.display, marginTop: 3, lineHeight: 1 }}>
              {fgLabel || "—"}
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 34, fontWeight: 800, color: fgVal == null ? C.dim : fgVal < 25 ? C.rd : fgVal < 45 ? "#ff8800" : fgVal < 55 ? C.yl : C.gn, fontFamily: C.mono, lineHeight: 1 }}>{fgVal != null ? fgVal : "—"}</div>
            <div style={{ fontSize: 9, color: C.mut, fontFamily: C.mono, textTransform: "uppercase", letterSpacing: "0.08em", marginTop: 4 }}>Fear &amp; Greed</div>
          </div>
        </div>
        {/* gradient bar with marker */}
        <div style={{ height: 6, borderRadius: 6, background: "linear-gradient(90deg, #ef4444, #eab308 45%, #10b981)", position: "relative", marginBottom: 14 }}>
          <div style={{ position: "absolute", top: -3, left: (fgVal != null ? Math.max(2, Math.min(98, fgVal)) : 50) + "%", width: 3, height: 12, background: "#fff", borderRadius: 2, boxShadow: "0 0 6px rgba(255,255,255,0.7)", transition: "left 1s ease" }} />
        </div>
        {/* turn-watch chips */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {[
            { k: "Sentiment", v: scan?.marketSentiment?.sentimentLabel || "—", dot: scan?.marketSentiment?.sentimentLabel === "Bullish" ? C.gn : scan?.marketSentiment?.sentimentLabel === "Bearish" ? C.rd : C.yl },
            { k: "Signals (scan)", v: String(scan?.qualifiedCount ?? "—"), dot: (scan?.qualifiedCount || 0) > 0 ? C.gn : C.mut },
            { k: "Verdicts (recent)", v: String(verdicts ? verdicts.length : 0), dot: verdicts && verdicts.length ? C.cy : C.mut },
          ].map((chip, i) => (
            <div key={i} style={{ flex: 1, minWidth: 90, background: C.inner, border: "1px solid " + C.bdr, borderRadius: 10, padding: "9px 10px" }}>
              <div style={{ fontSize: 9, color: C.mut, fontFamily: C.mono, textTransform: "uppercase", letterSpacing: "0.06em" }}>{chip.k}</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: C.tx, fontFamily: C.mono, marginTop: 3, display: "flex", alignItems: "center", gap: 5 }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: chip.dot, display: "inline-block" }} />{chip.v}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* FE-APPROVALS v1 — the approval wall */}
      {apprTrades.length > 0 && (() => {
        const rOf = (t) => {
          if (t.entry != null && t.stopLoss != null && t.closePrice != null) {
            const risk = t.direction === "LONG" ? t.entry - t.stopLoss : t.stopLoss - t.entry;
            if (risk > 0) {
              const mv = t.direction === "LONG" ? t.closePrice - t.entry : t.entry - t.closePrice;
              const r = mv / risk;
              if (isFinite(r)) return Math.round(Math.max(-10, Math.min(10, r)) * 100) / 100;
            }
          }
          return null;
        };
        const ago = (ms) => {
          const m = Math.max(1, Math.round((Date.now() - ms) / 60000));
          return m < 60 ? m + "m ago" : m < 1440 ? Math.round(m / 60) + "h ago" : Math.round(m / 1440) + "d ago";
        };
        return (
          <div style={{ marginBottom: 16 }}>
            <style>{"@keyframes bsPulse{0%{opacity:1}50%{opacity:0.35}100%{opacity:1}}"}</style>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10, padding: "0 2px" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.gn, fontFamily: C.display, textTransform: "uppercase", letterSpacing: "0.1em" }}>
                ✅ Approved by Bitscanner AI
              </div>
              <div style={{ fontSize: 10, color: C.mut, fontFamily: C.mono }}>{apprTrades.length} distinct</div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {apprTrades.slice(0, 6).map((t, i) => {
                const closed = t.status === "closed";
                const r = closed ? rOf(t) : null;
                const won = closed && t.outcome === "win";
                const lic = /cell license/i.test(t.claudeReasoning || "");
                const sc = closed ? (won ? C.gn : t.outcome === "loss" ? C.rd : C.yl) : C.gn;
                return (
                  <div key={i} style={{
                    background: "linear-gradient(135deg, " + C.gn + "16, transparent 55%), " + C.card,
                    border: "1px solid " + C.gn + "50", borderRadius: 14, padding: 14,
                    boxShadow: "0 0 24px " + C.gn + "1c",
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <span style={{ width: 26, height: 26, borderRadius: "50%", background: C.gn + "22", border: "1px solid " + C.gn + "66", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 13 }}>✓</span>
                        <span style={{ fontSize: 16, fontWeight: 800, color: C.tx, fontFamily: C.mono }}>{cleanSym(t.symbol)}</span>
                        <Badge type={t.direction === "LONG" ? "long" : "short"}>{t.direction}</Badge>
                        {lic && <span style={{ fontSize: 9, fontWeight: 800, fontFamily: C.mono, letterSpacing: "0.06em", padding: "3px 8px", borderRadius: 6, background: C.yl + "1a", color: C.yl, border: "1px solid " + C.yl + "55" }}>CELL LICENSE</span>}
                        {t._accounts > 1 && <span style={{ fontSize: 9, fontWeight: 700, fontFamily: C.mono, padding: "3px 8px", borderRadius: 6, background: C.inner, color: C.mut, border: "1px solid " + C.bdr }}>×{t._accounts} accounts</span>}
                      </div>
                      <span style={{ fontSize: 11, fontWeight: 800, fontFamily: C.mono, padding: "4px 10px", borderRadius: 6, background: sc + "18", color: sc, border: "1px solid " + sc + "44", display: "inline-flex", alignItems: "center", gap: 6 }}>
                        {!closed && <span style={{ width: 7, height: 7, borderRadius: "50%", background: C.gn, display: "inline-block", animation: "bsPulse 1.4s ease infinite" }} />}
                        {closed ? (won ? "WON" + (r != null ? " +" + r + "R" : "") : t.outcome === "loss" ? "LOST" + (r != null ? " " + r + "R" : "") : "FLAT") : "LIVE"}
                      </span>
                    </div>
                    <div style={{ display: "flex", gap: 14, margin: "10px 0 9px", flexWrap: "wrap" }}>
                      {t.confidence != null && <span style={{ fontSize: 11, color: C.mut, fontFamily: C.mono }}>Conf <b style={{ color: C.tx }}>{t.confidence}/10</b></span>}
                      {t.mlScore != null && <span style={{ fontSize: 11, color: C.mut, fontFamily: C.mono }}>ML <b style={{ color: C.tx }}>{Math.round(Number(t.mlScore) * 100)}%</b></span>}
                      {t.claudeExpectedR != null && <span style={{ fontSize: 11, color: C.mut, fontFamily: C.mono }}>Exp <b style={{ color: C.gn }}>+{t.claudeExpectedR}R</b></span>}
                      {t.kellyRiskPct != null && <span style={{ fontSize: 11, color: C.mut, fontFamily: C.mono }}>Kelly <b style={{ color: C.tx }}>{t.kellyRiskPct}%</b></span>}
                    </div>
                    {t.claudeReasoning && (
                      <div style={{ fontSize: 12.5, lineHeight: 1.6, color: C.dim, fontFamily: C.sans, borderTop: "1px solid " + C.gn + "26", paddingTop: 9 }}>
                        <span style={{ color: C.gn, fontWeight: 700, fontFamily: C.mono, fontSize: 10, letterSpacing: "0.08em" }}>WHY THE AI ENTERED · </span>
                        {t.claudeReasoning}
                      </div>
                    )}
                    <div style={{ display: "flex", justifyContent: "space-between", marginTop: 9, fontSize: 10, color: C.mut, fontFamily: C.mono }}>
                      <span>entry {t.entry} · SL {t.stopLoss}{t.tp2 ? " · TP " + t.tp2 : ""}</span>
                      <span>{ago(t.openedAt || 0)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}
      <RefusalTicker />
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10, padding: "0 2px" }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.or, fontFamily: C.display, textTransform: "uppercase", letterSpacing: "0.1em" }}>
          Bitscanner AI · Live Verdicts
        </div>
        <div style={{ fontSize: 10, color: C.mut, fontFamily: C.mono }}>
          {verdicts ? verdicts.length : 0} recent
        </div>
      </div>

      {loading && !verdicts ? (
        <Loader text="Loading Bitscanner AI verdicts..." />
      ) : !verdicts || verdicts.length === 0 ? (
        <Card style={{ textAlign: "center", padding: 40 }}>
          <div style={{ fontSize: 28, marginBottom: 10, opacity: 0.5 }}>🧠</div>
          <div style={{ fontSize: 14, color: C.dim, fontWeight: 600, fontFamily: C.sans }}>No recent verdicts</div>
          <div style={{ fontSize: 11, color: C.mut, marginTop: 4, fontFamily: C.sans, lineHeight: 1.5 }}>
            Bitscanner AI hasn't evaluated a signal recently.<br />Verdicts appear here as the scanner finds setups.
          </div>
        </Card>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {verdicts.map((v, i) => {
            const approved = v.approved === true;
            const dir = (v.direction || "").toUpperCase();
            const conf = v.confidence != null ? v.confidence : null;
            return (
              <Card key={i} accent={approved ? C.gn : C.rd}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 15, fontWeight: 800, color: C.tx, fontFamily: C.mono }}>{cleanSym(v.symbol)}</span>
                    <Badge type={dir === "LONG" ? "long" : "short"}>{dir}</Badge>
                    {v.strategy && <Badge type="info">{v.strategy}</Badge>}
                    {v.cached && <Badge type="neutral">cached</Badge>}
                  </div>
                  <span style={{
                    fontSize: 11, fontWeight: 800, fontFamily: C.mono, letterSpacing: "0.03em",
                    padding: "4px 10px", borderRadius: 6,
                    background: approved ? C.gn + "18" : C.rd + "15",
                    color: approved ? C.gn : C.rd,
                    border: "1px solid " + (approved ? C.gn : C.rd) + "40",
                  }}>{approved ? "APPROVED" : "REJECTED"}</span>
                </div>

                <div style={{ display: "flex", gap: 14, marginBottom: 9, flexWrap: "wrap" }}>
                  {conf != null && <span style={{ fontSize: 11, color: C.mut, fontFamily: C.mono }}>Conf <b style={{ color: C.tx }}>{conf}/10</b></span>}
                  {v.expectedR != null && <span style={{ fontSize: 11, color: C.mut, fontFamily: C.mono }}>Exp <b style={{ color: v.expectedR >= 0 ? C.gn : C.rd }}>{v.expectedR > 0 ? "+" : ""}{v.expectedR}R</b></span>}
                </div>

                {v.reasoning && (
                  <div style={{ fontSize: 12.5, lineHeight: 1.55, color: C.dim, fontFamily: C.sans, borderTop: "1px solid " + C.bdr, paddingTop: 9 }}>
                    {v.reasoning}
                  </div>
                )}

                {v.concerns && v.concerns.length > 0 && (
                  <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 3 }}>
                    {v.concerns.map((c, j) => (
                      <div key={j} style={{ fontSize: 10.5, color: C.mut, fontFamily: C.sans }}>
                        <span style={{ color: C.yl }}>⚠</span> {c}
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

function AdminTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAdmin = async () => {
      setLoading(true);
      const r = await api("/api/admin/performance");
      if (r.ok) setData(r.data);
      setLoading(false);
    };
    fetchAdmin();
    const t = setInterval(fetchAdmin, 60000);
    return () => clearInterval(t);
  }, []);

  if (loading && !data) return <Loader text="Loading admin data..." />;
  if (!data) return <Card><div style={{ textAlign: "center", color: C.dim, padding: 20 }}>Failed to load</div></Card>;

  const { summary, userStats, equityCurve, topSymbols, strategyBreakdown, claudeBudget } = data;

  return <div>
    <Card style={{ marginBottom: 14, borderColor: C.pu + "30" }} glow={C.pu}>
      <Stitle icon="👑" color={C.pu}>Platform Performance</Stitle>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 8 }}>
        {[{ l: "Users", v: summary.totalUsers, c: C.pu }, { l: "Closed", v: summary.totalClosed, c: C.tx }, { l: "Open", v: summary.totalOpen, c: C.or }].map(x => (
          <div key={x.l} style={{ background: C.inner, borderRadius: 8, padding: 10, border: "1px solid " + C.bdr }}>
            <div style={{ fontSize: 8, color: C.mut, textTransform: "uppercase", fontFamily: C.sans }}>{x.l}</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: x.c, fontFamily: C.mono }}>{x.v}</div>
          </div>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <div style={{ background: C.inner, borderRadius: 8, padding: 10, border: "1px solid " + C.bdr }}>
          <div style={{ fontSize: 8, color: C.mut, textTransform: "uppercase", fontFamily: C.sans }}>Win Rate</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: summary.winRate >= 55 ? C.gn : summary.winRate >= 45 ? C.yl : C.rd, fontFamily: C.mono }}>{summary.winRate.toFixed(0)}%</div>
        </div>
        <div style={{ background: C.inner, borderRadius: 8, padding: 10, border: "1px solid " + C.bdr }}>
          <div style={{ fontSize: 8, color: C.mut, textTransform: "uppercase", fontFamily: C.sans }}>Total P&L</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: summary.totalPnl >= 0 ? C.gn : C.rd, fontFamily: C.mono }}>{summary.totalPnl >= 0 ? "+" : "-"}${fU(Math.abs(summary.totalPnl)).replace("$","")}</div>
        </div>
      </div>
    </Card>

    {claudeBudget && (
      <Card style={{ marginBottom: 14, borderColor: C.cy + "30" }}>
        <Stitle icon="🧠" color={C.cy}>Bitscanner AI Spend (Today)</Stitle>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 8 }}>
          <div style={{ background: C.inner, borderRadius: 8, padding: 10, border: "1px solid " + C.bdr }}>
            <div style={{ fontSize: 8, color: C.mut, textTransform: "uppercase", fontFamily: C.sans }}>Spent</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: C.cy, fontFamily: C.mono }}>{"$" + (claudeBudget.spentUSD || 0).toFixed(2)}</div>
          </div>
          <div style={{ background: C.inner, borderRadius: 8, padding: 10, border: "1px solid " + C.bdr }}>
            <div style={{ fontSize: 8, color: C.mut, textTransform: "uppercase", fontFamily: C.sans }}>Daily Cap</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: C.tx, fontFamily: C.mono }}>{"$" + (claudeBudget.capUSD || 0).toFixed(2)}</div>
          </div>
          <div style={{ background: C.inner, borderRadius: 8, padding: 10, border: "1px solid " + C.bdr }}>
            <div style={{ fontSize: 8, color: C.mut, textTransform: "uppercase", fontFamily: C.sans }}>Calls</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: C.tx, fontFamily: C.mono }}>{claudeBudget.calls || 0}</div>
          </div>
        </div>
        <div style={{ height: 6, borderRadius: 6, background: C.inner, overflow: "hidden", border: "1px solid " + C.bdr }}>
          <div style={{ height: "100%", width: Math.min(100, claudeBudget.pctUsed || 0) + "%", background: (claudeBudget.pctUsed || 0) >= 80 ? C.rd : (claudeBudget.pctUsed || 0) >= 50 ? C.yl : C.cy }} />
        </div>
        <div style={{ fontSize: 9, color: C.mut, fontFamily: C.mono, marginTop: 5, textAlign: "right" }}>
          {(claudeBudget.pctUsed || 0).toFixed(1)}% of daily cap{claudeBudget.override ? " · OVERRIDE ON" : ""}
        </div>
      </Card>
    )}
    {equityCurve.length > 2 && (
      <Card style={{ marginBottom: 14 }}>
        <Stitle icon="📈">Combined Equity Curve</Stitle>
        <div style={{ height: 140 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={equityCurve}>
              <Line type="monotone" dataKey="equity" stroke={summary.totalPnl >= 0 ? C.gn : C.rd} strokeWidth={2} dot={false} />
              <Tooltip contentStyle={{ background: C.card, border: "1px solid " + C.bdr, borderRadius: 8, fontFamily: C.mono, fontSize: 11 }}
                formatter={(v) => ["$" + v.toFixed(2), "P&L"]}
                labelFormatter={(l) => "Trade #" + l} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>
    )}

    <CollapsibleSection icon="👥" title="User breakdown" color={C.bl} badge={userStats.length + " users"} badgeColor={C.bl} defaultOpen={false}>
    {userStats.map(u => (
      <Card key={u.username} style={{ marginBottom: 12 }} accent={u.totalPnl >= 0 ? C.gn : C.rd}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: C.pu + "15", border: "1px solid " + C.pu + "30", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>👤</div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 800, color: C.tx, fontFamily: C.mono }}>{u.username}</div>
              <div style={{ fontSize: 9, color: C.dim, fontFamily: C.sans }}>{"$" + u.balance + " · " + u.riskPct + "% risk · " + u.leverage + "x · " + ((u.exchange === "Bybit" ? u.bybitKeysConfigured : u.keysConfigured) ? "🟢" : "🔴") + " keys"}</div>
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: u.totalPnl >= 0 ? C.gn : C.rd, fontFamily: C.mono }}>{(u.totalPnl >= 0 ? "+$" : "-$") + Math.abs(u.totalPnl).toFixed(2)}</div>
            <div style={{ fontSize: 10, color: C.dim, fontFamily: C.mono }}>{u.openPositions + "/" + (u.maxOpenTrades || 5) + " open"}</div>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 6, marginBottom: 10 }}>
          {[{ l: "Trades", v: u.totalTrades, c: C.tx }, { l: "Win %", v: u.winRate.toFixed(0) + "%", c: u.winRate >= 55 ? C.gn : u.winRate >= 45 ? C.yl : C.rd }, { l: "Avg Win", v: fU(Math.abs(u.avgWin)), c: C.gn }, { l: "Avg Loss", v: fU(Math.abs(u.avgLoss)), c: C.rd }].map(x => (
            <div key={x.l} style={{ background: C.inner, borderRadius: 6, padding: 8, border: "1px solid " + C.bdr }}>
              <div style={{ fontSize: 8, color: C.mut, textTransform: "uppercase", fontFamily: C.sans }}>{x.l}</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: x.c, fontFamily: C.mono }}>{x.v}</div>
            </div>
          ))}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 10 }}>
          {[{ type: "long", label: "LONG", stats: u.longStats }, { type: "short", label: "SHORT", stats: u.shortStats }].map(s => (
            <div key={s.type} style={{ background: C.inner, borderRadius: 6, padding: 8, border: "1px solid " + C.bdr, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <Badge type={s.type}>{s.label}</Badge>
                <div style={{ fontSize: 10, color: C.dim, fontFamily: C.mono, marginTop: 4 }}>{s.stats.trades + " trades · " + s.stats.winRate.toFixed(0) + "%"}</div>
              </div>
              <div style={{ fontSize: 13, fontWeight: 700, color: s.stats.pnl >= 0 ? C.gn : C.rd, fontFamily: C.mono }}>{(s.stats.pnl >= 0 ? "+" : "") + "$" + s.stats.pnl.toFixed(2)}</div>
            </div>
          ))}
        </div>

        {(u.bestTrade || u.worstTrade) && (
          <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
            {u.bestTrade && <div style={{ flex: 1, background: C.gn + "08", borderRadius: 6, padding: 8, border: "1px solid " + C.gn + "20" }}>
              <div style={{ fontSize: 8, color: C.gn, textTransform: "uppercase", fontFamily: C.sans }}>Best Trade</div>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.gn, fontFamily: C.mono }}>{cleanSym(u.bestTrade.symbol) + " " + (u.bestTrade.pnl >= 0 ? "+$" : "-$") + Math.abs(u.bestTrade.pnl).toFixed(2)}</div>
            </div>}
            {u.worstTrade && <div style={{ flex: 1, background: C.rd + "08", borderRadius: 6, padding: 8, border: "1px solid " + C.rd + "20" }}>
              <div style={{ fontSize: 8, color: C.rd, textTransform: "uppercase", fontFamily: C.sans }}>Worst Trade</div>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.rd, fontFamily: C.mono }}>{cleanSym(u.worstTrade.symbol) + " -$" + Math.abs(u.worstTrade.pnl).toFixed(2)}</div>
            </div>}
          </div>
        )}

        {u.openTrades.length > 0 && (
          <div style={{ marginBottom: 6 }}>
            <div style={{ fontSize: 9, color: C.mut, textTransform: "uppercase", fontFamily: C.sans, marginBottom: 4 }}>Open Positions</div>
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
              {u.openTrades.map((t, i) => (
                <div key={i} style={{ background: C.inner, borderRadius: 6, padding: "4px 8px", border: "1px solid " + C.bdr, display: "flex", alignItems: "center", gap: 4 }}>
                  <span style={{ fontSize: 10, fontWeight: 700, fontFamily: C.mono, color: C.tx }}>{cleanSym(t.symbol)}</span>
                  <Badge type={t.direction === "LONG" ? "long" : "short"}>{t.direction}</Badge>
                </div>
              ))}
            </div>
          </div>
        )}

        {u.recentTrades.length > 0 && (
          <details style={{ marginTop: 8 }}>
            <summary style={{ fontSize: 9, color: C.mut, textTransform: "uppercase", fontFamily: C.sans, cursor: "pointer" }}>Recent Trades ({u.recentTrades.length})</summary>
            <div style={{ marginTop: 6 }}>
              {u.recentTrades.map((t, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "4px 0", borderBottom: "1px solid " + C.inner }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <Badge type={t.outcome === "win" ? "win" : "loss"}>{t.outcome}</Badge>
                    <span style={{ fontSize: 10, fontWeight: 700, fontFamily: C.mono, color: C.tx }}>{cleanSym(t.symbol)}</span>
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 700, fontFamily: C.mono, color: t.pnl >= 0 ? C.gn : C.rd }}>{(t.pnl >= 0 ? "+" : "") + "$" + t.pnl.toFixed(2)}</span>
                </div>
              ))}
            </div>
          </details>
        )}
      </Card>
    ))}

    </CollapsibleSection>

    {/* System Status Bar */}
    <SystemStatusBar />
    <PaperTradingCard />
    <BybitShadowCard />

    {/* Collapsible: Strategy & Symbols */}
    <CollapsibleSection icon="🧠" title="Strategy & symbols" color={C.pu} badge={strategyBreakdown.length + " strategies"} badgeColor={C.yl}>
      {strategyBreakdown.length > 0 && <Card style={{ borderRadius: 0, borderTop: "none" }}>
        <Stitle icon="📊">Strategy Breakdown</Stitle>
        {strategyBreakdown.map((s, i) => (
          <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid " + C.inner }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, fontFamily: C.mono, color: C.tx }}>{s.strategy}</div>
              <div style={{ fontSize: 9, color: C.dim, fontFamily: C.sans }}>{s.count + " trades · " + s.wins + "W / " + s.losses + "L · " + s.winRate.toFixed(0) + "%"}</div>
            </div>
            <div style={{ fontSize: 14, fontWeight: 700, fontFamily: C.mono, color: s.pnl >= 0 ? C.gn : C.rd }}>{(s.pnl >= 0 ? "+" : "") + "$" + s.pnl.toFixed(2)}</div>
          </div>
        ))}
      </Card>}
      {topSymbols.length > 0 && <Card style={{ borderRadius: "0 0 12px 12px", borderTop: "none" }}>
        <Stitle icon="🏆">Top Symbols</Stitle>
        {topSymbols.map((s, i) => (
          <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: "1px solid " + C.inner }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 10, color: C.mut, fontFamily: C.mono, width: 18 }}>{i + 1}.</span>
              <span style={{ fontSize: 12, fontWeight: 700, fontFamily: C.mono, color: C.tx }}>{cleanSym(s.symbol)}</span>
              <span style={{ fontSize: 9, color: C.dim, fontFamily: C.sans }}>{s.count + " trades · " + s.winRate.toFixed(0) + "%"}</span>
            </div>
            <span style={{ fontSize: 12, fontWeight: 700, fontFamily: C.mono, color: s.pnl >= 0 ? C.gn : C.rd }}>{(s.pnl >= 0 ? "+" : "") + "$" + s.pnl.toFixed(2)}</span>
          </div>
        ))}
      </Card>}
    </CollapsibleSection>

    {/* Collapsible: Intelligence */}
    <CollapsibleSection icon="🤖" title="Intelligence" color={C.cy} badge="ML + Patterns" badgeColor={C.cy}>
      <MLModelView />
      <PatternView />
    </CollapsibleSection>

    {/* Collapsible: Audit Log */}
    <CollapsibleSection icon="📋" title="Audit log" color={C.bl} badge="Events" badgeColor={C.bl}>
      <AuditLog />
    </CollapsibleSection>
  </div>;
}


function PatternView() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    const fetchPatterns = async () => {
      const r = await api("/api/admin/patterns");
      if (r.ok) setData(r.data);
      setLoading(false);
    };
    fetchPatterns();
    const t = setInterval(fetchPatterns, 120000);
    return () => clearInterval(t);
  }, []);

  if (loading && !data) return <Loader text="Loading patterns..." />;
  if (!data) return null;

  const { patterns, symbols } = data;
  const shown = showAll ? patterns : patterns.slice(0, 12);

  return <div>
    <Card style={{ marginBottom: 14 }}>
      <Stitle icon="🧠" color={C.pu}>Tier 2 — Pattern Analysis</Stitle>
      <div style={{ fontSize: 10, color: C.dim, fontFamily: C.sans, marginBottom: 12 }}>Patterns from closed trades. Green = boosted, Red = penalised/blocked.</div>
      {shown.map((p, i) => {
        const color = p.winRate >= 55 ? C.gn : p.winRate <= 35 ? C.rd : C.yl;
        const icon = p.winRate >= 55 ? "🟢" : p.winRate <= 35 ? "🔴" : "🟡";
        const parts = p.pattern.split(":");
        return (
          <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: "1px solid " + C.inner }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
                <span>{icon}</span>
                {parts.map((pt, j) => (
                  <span key={j} style={{ fontSize: 9, padding: "2px 6px", background: C.inner, borderRadius: 4, border: "1px solid " + C.bdr, color: C.dim, fontFamily: C.mono }}>{pt}</span>
                ))}
              </div>
              <div style={{ fontSize: 9, color: C.mut, fontFamily: C.sans, marginTop: 3 }}>{p.count + " trades · " + p.wins + "W / " + p.losses + "L · $" + p.pnl.toFixed(2)}</div>
            </div>
            <div style={{ textAlign: "right", flexShrink: 0, marginLeft: 8 }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: color, fontFamily: C.mono }}>{p.winRate + "%"}</div>
              <div style={{ fontSize: 8, color: C.mut, fontFamily: C.sans }}>{"$" + p.expectancy.toFixed(2) + "/trade"}</div>
            </div>
          </div>
        );
      })}
      {patterns.length > 12 && (
        <button onClick={() => setShowAll(!showAll)} style={{ width: "100%", padding: 8, marginTop: 8, background: C.inner, border: "1px solid " + C.bdr, borderRadius: 6, color: C.dim, cursor: "pointer", fontFamily: C.sans, fontSize: 10 }}>{showAll ? "Show less" : "Show all " + patterns.length + " patterns"}</button>
      )}
    </Card>

    <Card style={{ marginBottom: 14 }}>
      <Stitle icon="📊">Symbol Performance</Stitle>
      {symbols.slice(0, 15).map((s, i) => (
        <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "5px 0", borderBottom: "1px solid " + C.inner }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 10, color: C.mut, fontFamily: C.mono, width: 18 }}>{i + 1 + "."}</span>
            <span style={{ fontSize: 11, fontWeight: 700, fontFamily: C.mono, color: C.tx }}>{s.symbol}</span>
            <span style={{ fontSize: 9, color: C.dim, fontFamily: C.sans }}>{s.count + " trades · " + s.winRate + "%"}</span>
          </div>
          <span style={{ fontSize: 12, fontWeight: 700, fontFamily: C.mono, color: s.pnl >= 0 ? C.gn : C.rd }}>{(s.pnl >= 0 ? "+" : "") + "$" + s.pnl.toFixed(2)}</span>
        </div>
      ))}
    </Card>
  </div>;
}


function MLModelView() {
  const [health, setHealth] = useState(null);
  const [importance, setImportance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [retraining, setRetraining] = useState(false);

  useEffect(() => {
    const fetchML = async () => {
      try {
        const h = await api("/api/admin/ml-health");
        if (h.ok) setHealth(h.data);
        const imp = await api("/api/admin/ml-importance");
        if (imp.ok) setImportance(imp.data);
      } catch (e) {}
      setLoading(false);
    };
    fetchML();
    const t = setInterval(fetchML, 60000);
    return () => clearInterval(t);
  }, []);

  const handleRetrain = async () => {
    setRetraining(true);
    const r = await api("/api/admin/ml-retrain", { method: "POST" });
    if (r.ok) {
      const h = await api("/api/admin/ml-health");
      if (h.ok) setHealth(h.data);
      const imp = await api("/api/admin/ml-importance");
      if (imp.ok) setImportance(imp.data);
    }
    setRetraining(false);
  };

  if (loading) return <Loader text="Loading ML data..." />;

  const meta = health?.model_meta || {};
  const features = importance?.features || [];
  const maxImp = features.length > 0 ? features[0].importance : 1;

  return <div>
    <Card style={{ marginBottom: 14, borderColor: C.cy + "30" }} glow={C.cy}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <Stitle icon="🤖" color={C.cy}>Tier 3 — ML Model</Stitle>
        <button onClick={handleRetrain} disabled={retraining} style={{ padding: "6px 12px", background: retraining ? C.inner : C.cy + "15", border: "1px solid " + (retraining ? C.bdr : C.cy), borderRadius: 6, color: retraining ? C.dim : C.cy, cursor: retraining ? "wait" : "pointer", fontFamily: C.mono, fontSize: 9, fontWeight: 700 }}>{retraining ? "Training..." : "Retrain Now"}</button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 6, marginBottom: 12 }}>
        {[
          { l: "Status", v: health?.status === "ok" ? "Online" : "Offline", c: health?.status === "ok" ? C.gn : C.rd },
          { l: "Accuracy", v: ((meta.cv_accuracy || 0) * 100).toFixed(1) + "%", c: (meta.cv_accuracy || 0) >= 0.5 ? C.gn : C.yl },
          { l: "Features", v: String(meta.feature_count || 0), c: C.tx },
          { l: "Trades", v: String(meta.trade_count || 0), c: C.tx },
        ].map(x => (
          <div key={x.l} style={{ background: C.inner, borderRadius: 6, padding: 8, border: "1px solid " + C.bdr }}>
            <div style={{ fontSize: 8, color: C.mut, textTransform: "uppercase", fontFamily: C.sans }}>{x.l}</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: x.c, fontFamily: C.mono }}>{x.v}</div>
          </div>
        ))}
      </div>

      {meta.trained_at && (
        <div style={{ fontSize: 9, color: C.dim, fontFamily: C.sans, marginBottom: 12 }}>{"Last trained: " + new Date(meta.trained_at).toLocaleString() + " · Model: " + (meta.model_version || "?")}</div>
      )}
    </Card>

    {features.length > 0 && (
      <Card style={{ marginBottom: 14 }}>
        <Stitle icon="📊">Feature Importance</Stitle>
        <div style={{ fontSize: 10, color: C.dim, fontFamily: C.sans, marginBottom: 10 }}>Which features the ML model uses most to predict wins vs losses.</div>
        {features.slice(0, 15).map((f, i) => {
          const pct = (f.importance / maxImp) * 100;
          return (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <span style={{ fontSize: 9, color: C.dim, fontFamily: C.mono, width: 18, textAlign: "right" }}>{i + 1}</span>
              <span style={{ fontSize: 10, fontWeight: 600, color: C.tx, fontFamily: C.mono, width: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</span>
              <div style={{ flex: 1, height: 14, background: C.inner, borderRadius: 4, overflow: "hidden", border: "1px solid " + C.bdr }}>
                <div style={{ width: pct + "%", height: "100%", background: "linear-gradient(90deg, " + C.cy + "40, " + C.cy + ")", borderRadius: 4, transition: "width 0.5s" }} />
              </div>
              <span style={{ fontSize: 9, color: C.dim, fontFamily: C.mono, width: 40, textAlign: "right" }}>{(f.importance * 100).toFixed(1) + "%"}</span>
            </div>
          );
        })}
      </Card>
    )}
  </div>;
}

function WSMonitor() {
  const [ws, setWs] = useState(null);
  useEffect(() => {
    const fetch = async () => { const r = await api("/api/admin/ws-status"); if (r.ok) setWs(r.data); };
    fetch();
    const iv = setInterval(fetch, 15000);
    return () => clearInterval(iv);
  }, []);
  if (!ws) return null;
  return <Card style={{ marginBottom: 14, borderColor: (ws.connected ? C.gn : C.rd) + "30" }} glow={ws.connected ? C.gn : C.rd}>
    <Stitle icon="📡" color={ws.connected ? C.gn : C.rd}>WebSocket Monitor</Stitle>
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: ws.symbols.length > 0 ? 10 : 0 }}>
      {[
        { l: "Status", v: ws.connected ? "Connected" : "Disconnected", c: ws.connected ? C.gn : C.rd },
        { l: "Watching", v: ws.symbolCount + " symbols", c: ws.symbolCount > 0 ? C.cy : C.dim },
      ].map(x => (
        <div key={x.l} style={{ background: C.inner, borderRadius: 6, padding: 8, border: "1px solid " + C.bdr }}>
          <div style={{ fontSize: 8, color: C.mut, textTransform: "uppercase", fontFamily: C.sans }}>{x.l}</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: x.c, fontFamily: C.mono }}>{x.v}</div>
        </div>
      ))}
    </div>
    {ws.symbols.length > 0 && <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
      {ws.symbols.map(s => <span key={s} style={{ fontSize: 9, fontFamily: C.mono, background: C.cy + "15", color: C.cy, border: "1px solid " + C.cy + "30", borderRadius: 4, padding: "2px 6px" }}>{s.replace("USDT","")}</span>)}
    </div>}
  </Card>;
}


function BybitShadowCard() {
  const [expanded, setExpanded] = useState(false);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      const r = await api("/api/admin/bybit-shadow-stats");
      if (r.ok) setStats(r.data);
    };
    fetchStats();
    const iv = setInterval(fetchStats, 60000);
    return () => clearInterval(iv);
  }, []);

  if (!stats) return null;
  const wr = parseFloat(stats.winRate) || 0;
  const pnl = parseFloat(stats.totalPnl) || 0;
  const avgPnl = parseFloat(stats.avgPnl) || 0;
  const todayPnl = parseFloat(stats.today.pnl) || 0;
  const weekPnl = parseFloat(stats.week.pnl) || 0;
  const monthPnl = parseFloat(stats.month.pnl) || 0;

  return <Card style={{ marginBottom: 10, borderColor: C.bl + "30" }} glow={C.bl}>
    <div onClick={() => setExpanded(!expanded)} style={{ cursor: "pointer" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 13 }}>🤖</span>
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.5px", color: C.bl, fontFamily: C.sans }}>BYBIT SHADOW (ML ENGINE)</span>
          <span style={{ display: "inline-flex", padding: "2px 8px", borderRadius: 999, fontSize: 9, fontWeight: 700, fontFamily: C.mono, background: C.bl + "15", color: C.bl, border: "1px solid " + C.bl + "30" }}>{stats.open + "/" + stats.cap}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 9, fontWeight: 700, color: pnl >= 0 ? C.gn : C.rd, fontFamily: C.mono }}>{(pnl >= 0 ? "+" : "") + "$" + Math.abs(pnl).toFixed(2)}</span>
          <span style={{ fontSize: 9, color: C.dim, fontFamily: C.mono }}>{wr.toFixed(0) + "% WR"}</span>
          <span style={{ fontSize: 14, color: C.mut, transform: expanded ? "rotate(90deg)" : "none", transition: "transform 0.2s" }}>{String.fromCharCode(9656)}</span>
        </div>
      </div>
    </div>
    {expanded && <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid " + C.bdr }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr", gap: 6, marginBottom: 10 }}>
        {[
          { l: "Open", v: String(stats.open), c: C.or },
          { l: "Closed", v: String(stats.closed), c: C.tx },
          { l: "Win Rate", v: wr.toFixed(1) + "%", c: wr >= 50 ? C.gn : wr >= 40 ? C.yl : C.rd },
          { l: "Wins", v: String(stats.wins), c: C.gn },
          { l: "Losses", v: String(stats.losses), c: C.rd },
        ].map(function(x) { return (
          <div key={x.l} style={{ background: C.inner, borderRadius: 6, padding: 8, border: "1px solid " + C.bdr }}>
            <div style={{ fontSize: 7, color: C.mut, textTransform: "uppercase", fontFamily: C.sans }}>{x.l}</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: x.c, fontFamily: C.mono }}>{x.v}</div>
          </div>
        ); })}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, marginBottom: 10 }}>
        <div style={{ background: C.inner, borderRadius: 6, padding: 8, border: "1px solid " + C.bdr }}>
          <div style={{ fontSize: 8, color: C.mut, textTransform: "uppercase", fontFamily: C.sans }}>Total P&L</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: pnl >= 0 ? C.gn : C.rd, fontFamily: C.mono }}>{(pnl >= 0 ? "+" : "") + "$" + Math.abs(pnl).toFixed(2)}</div>
        </div>
        <div style={{ background: C.inner, borderRadius: 6, padding: 8, border: "1px solid " + C.bdr }}>
          <div style={{ fontSize: 8, color: C.mut, textTransform: "uppercase", fontFamily: C.sans }}>Avg $/Trade</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: avgPnl >= 0 ? C.gn : C.rd, fontFamily: C.mono }}>{(avgPnl >= 0 ? "+" : "") + "$" + Math.abs(avgPnl).toFixed(2)}</div>
        </div>
        <div style={{ background: C.inner, borderRadius: 6, padding: 8, border: "1px solid " + C.bdr }}>
          <div style={{ fontSize: 8, color: C.mut, textTransform: "uppercase", fontFamily: C.sans }}>Today P&L</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: todayPnl >= 0 ? C.gn : C.rd, fontFamily: C.mono }}>{(todayPnl >= 0 ? "+" : "") + "$" + Math.abs(todayPnl).toFixed(2)}</div>
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, marginBottom: 10 }}>
        <div style={{ background: C.inner, borderRadius: 6, padding: 8, border: "1px solid " + C.bdr }}>
          <div style={{ fontSize: 8, color: C.mut, textTransform: "uppercase", fontFamily: C.sans }}>Today</div>
          <div style={{ fontSize: 11, fontFamily: C.mono, color: todayPnl >= 0 ? C.gn : C.rd }}>{stats.today.count + " trades, " + (todayPnl >= 0 ? "+" : "") + "$" + Math.abs(todayPnl).toFixed(0)}</div>
        </div>
        <div style={{ background: C.inner, borderRadius: 6, padding: 8, border: "1px solid " + C.bdr }}>
          <div style={{ fontSize: 8, color: C.mut, textTransform: "uppercase", fontFamily: C.sans }}>7 Days</div>
          <div style={{ fontSize: 11, fontFamily: C.mono, color: weekPnl >= 0 ? C.gn : C.rd }}>{stats.week.count + " trades, " + (weekPnl >= 0 ? "+" : "") + "$" + Math.abs(weekPnl).toFixed(0)}</div>
        </div>
        <div style={{ background: C.inner, borderRadius: 6, padding: 8, border: "1px solid " + C.bdr }}>
          <div style={{ fontSize: 8, color: C.mut, textTransform: "uppercase", fontFamily: C.sans }}>30 Days</div>
          <div style={{ fontSize: 11, fontFamily: C.mono, color: monthPnl >= 0 ? C.gn : C.rd }}>{stats.month.count + " trades, " + (monthPnl >= 0 ? "+" : "") + "$" + Math.abs(monthPnl).toFixed(0)}</div>
        </div>
      </div>
      <details style={{ marginBottom: 8 }}>
        <summary style={{ fontSize: 9, color: C.mut, textTransform: "uppercase", fontFamily: C.sans, cursor: "pointer" }}>Per Strategy</summary>
        <div style={{ marginTop: 6 }}>
          {Object.entries(stats.byStrategy).sort(function(a,b){ return b[1].count - a[1].count; }).map(function(arr) {
            var strat = arr[0], s = arr[1];
            var wrNum = parseFloat(s.winRate);
            var pnlNum = parseFloat(s.pnl);
            return <div key={strat} style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr 1fr 1fr", alignItems: "center", padding: "4px 8px", borderBottom: "1px solid " + C.inner, fontFamily: C.mono, fontSize: 10 }}>
              <span style={{ color: C.tx, fontWeight: 700 }}>{strat}</span>
              <span style={{ color: C.dim, textAlign: "right" }}>{s.count + " trades"}</span>
              <span style={{ color: wrNum >= 50 ? C.gn : wrNum >= 40 ? C.yl : C.rd, textAlign: "right" }}>{s.winRate + "% WR"}</span>
              <span style={{ color: pnlNum >= 0 ? C.gn : C.rd, textAlign: "right" }}>{(pnlNum >= 0 ? "+" : "") + "$" + Math.abs(pnlNum).toFixed(0)}</span>
            </div>;
          })}
        </div>
      </details>
      <details style={{ marginBottom: 8 }}>
        <summary style={{ fontSize: 9, color: C.mut, textTransform: "uppercase", fontFamily: C.sans, cursor: "pointer" }}>Per Confidence (ML correlation)</summary>
        <div style={{ marginTop: 6 }}>
          {Object.entries(stats.byConfidence).sort(function(a,b){ return parseInt(b[0]) - parseInt(a[0]); }).map(function(arr) {
            var conf = arr[0], s = arr[1];
            var wrNum = parseFloat(s.winRate);
            var pnlNum = parseFloat(s.pnl);
            return <div key={conf} style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr 1fr 1fr", alignItems: "center", padding: "4px 8px", borderBottom: "1px solid " + C.inner, fontFamily: C.mono, fontSize: 10 }}>
              <span style={{ color: C.tx, fontWeight: 700 }}>{"Conf " + conf}</span>
              <span style={{ color: C.dim, textAlign: "right" }}>{s.count + " trades"}</span>
              <span style={{ color: wrNum >= 50 ? C.gn : wrNum >= 40 ? C.yl : C.rd, textAlign: "right" }}>{s.winRate + "% WR"}</span>
              <span style={{ color: pnlNum >= 0 ? C.gn : C.rd, textAlign: "right" }}>{(pnlNum >= 0 ? "+" : "") + "$" + Math.abs(pnlNum).toFixed(0)}</span>
            </div>;
          })}
        </div>
      </details>
      {stats.liveBybit && !stats.liveBybit.error && <div style={{ background: C.inner, borderRadius: 6, padding: 8, border: "1px solid " + C.bdr, marginBottom: 8, fontSize: 9, fontFamily: C.mono }}>
        <div style={{ fontSize: 7, color: C.mut, textTransform: "uppercase", fontFamily: C.sans, marginBottom: 4 }}>Live Bybit Account</div>
        <div style={{ color: C.tx }}>Bal: ${(stats.liveBybit.balance || 0).toFixed(2)} | Eq: ${(stats.liveBybit.equity || 0).toFixed(2)} | Open: {stats.liveBybit.openPositions} | Unreal: <span style={{ color: (stats.liveBybit.totalUnrealized || 0) >= 0 ? C.gn : C.rd }}>${(stats.liveBybit.totalUnrealized || 0).toFixed(2)}</span></div>
      </div>}
      <div style={{ fontSize: 9, color: C.dim, fontFamily: C.sans }}>Exchange-verified closes across all three Bybit demo accounts (gate-approved executions). Totals from a persistent local ledger of Bybit\u2019s own records. Primary ML training source.{stats.duplicates > 0 ? " (" + stats.duplicates + " merged duplicates excluded)" : ""}</div>
    </div>}
  </Card>;
}

function PaperTradingCard() {
  const [stats, setStats] = useState(null);
  const [trades, setTrades] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [showOpen, setShowOpen] = useState(false);
  const [showClosed, setShowClosed] = useState(false);
  useEffect(() => {
    const fetchStats = async () => {
      const r = await api("/api/admin/paper-stats");
      if (r.ok) setStats(r.data);
      const tr = await api("/api/trades?all=1&limit=500"); // FRONTEND-BATCH v1: paper trades carry executedBy:"paper" — the user-scoped fetch excluded them
      if (tr.ok) setTrades((tr.data || []).filter(t => t.mode === "paper"));
    };
    fetchStats();
    const iv = setInterval(fetchStats, 60000); // FRONTEND-BATCH v1
    return () => clearInterval(iv);
  }, []);
  if (!stats) return null;
  const wr = parseFloat(stats.winRate) || 0;
  const pnl = parseFloat(stats.totalPnl) || 0;
  const openTrades = trades.filter(t => t.status === "open");
  const closedTrades = trades.filter(t => t.status === "closed").slice(-20).reverse();
  // FE-PAPERAVG v1: prefer the server’s honest avgPerTrade (realR x riskAmount,
  // full population) over local flat-pnl math on the fetch window.
  const avgPnl = stats.avgPerTrade != null
    ? (parseFloat(stats.avgPerTrade) || 0)
    : (closedTrades.length > 0 ? closedTrades.reduce((s, t) => s + (t.pnl || 0), 0) / closedTrades.length : 0);
  return <Card style={{ marginBottom: 10, borderColor: C.yl + "30" }} glow={C.yl}>
    <div onClick={() => setExpanded(!expanded)} style={{ cursor: "pointer" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 13 }}>{String.fromCodePoint(128196)}</span>
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.5px", color: C.yl, fontFamily: C.sans }}>PAPER TRADING</span>
          <span style={{ display: "inline-flex", padding: "2px 8px", borderRadius: 999, fontSize: 9, fontWeight: 700, fontFamily: C.mono, background: C.yl + "15", color: C.yl, border: "1px solid " + C.yl + "30" }}>{stats.open + "/" + stats.maxSlots}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 9, fontWeight: 700, color: pnl >= 0 ? C.gn : C.rd, fontFamily: C.mono }}>{(pnl >= 0 ? "+" : "") + "$" + Math.abs(pnl).toFixed(2)}</span>
          <span style={{ fontSize: 9, color: C.dim, fontFamily: C.mono }}>{wr.toFixed(0) + "% WR"}</span>
          <span style={{ fontSize: 14, color: C.mut, transform: expanded ? "rotate(90deg)" : "none", transition: "transform 0.2s" }}>{String.fromCharCode(9656)}</span>
        </div>
      </div>
    </div>
    {expanded && <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid " + C.bdr }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr", gap: 6, marginBottom: 10 }}>
        {[
          { l: "Open", v: String(stats.open), c: C.or },
          { l: "Closed", v: String(stats.closed), c: C.tx },
          { l: "Win Rate", v: wr.toFixed(1) + "%", c: wr >= 50 ? C.gn : wr >= 40 ? C.yl : C.rd },
          { l: "Wins", v: String(stats.wins), c: C.gn },
          { l: "Losses", v: String(stats.losses), c: C.rd },
        ].map(function(x) { return (
          <div key={x.l} style={{ background: C.inner, borderRadius: 6, padding: 8, border: "1px solid " + C.bdr }}>
            <div style={{ fontSize: 7, color: C.mut, textTransform: "uppercase", fontFamily: C.sans }}>{x.l}</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: x.c, fontFamily: C.mono }}>{x.v}</div>
          </div>
        ); })}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, marginBottom: 10 }}>
        <div style={{ background: C.inner, borderRadius: 6, padding: 8, border: "1px solid " + C.bdr }}>
          <div style={{ fontSize: 8, color: C.mut, textTransform: "uppercase", fontFamily: C.sans }}>Paper P&L</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: pnl >= 0 ? C.gn : C.rd, fontFamily: C.mono }}>{(pnl >= 0 ? "+" : "") + "$" + Math.abs(pnl).toFixed(2)}</div>
        </div>
        <div style={{ background: C.inner, borderRadius: 6, padding: 8, border: "1px solid " + C.bdr }}>
          <div style={{ fontSize: 8, color: C.mut, textTransform: "uppercase", fontFamily: C.sans }}>Avg $/Trade</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: avgPnl >= 0 ? C.gn : C.rd, fontFamily: C.mono }}>{(avgPnl >= 0 ? "+" : "") + "$" + Math.abs(avgPnl).toFixed(2)}</div>
        </div>
        <div style={{ background: C.inner, borderRadius: 6, padding: 8, border: "1px solid " + C.bdr }}>
          <div style={{ fontSize: 8, color: C.mut, textTransform: "uppercase", fontFamily: C.sans }}>Budget</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: C.yl, fontFamily: C.mono }}>$10K</div>
        </div>
      </div>
      <div style={{ fontSize: 9, color: C.dim, fontFamily: C.sans, marginBottom: 10 }}>Control group: simulated trades on ALL signals (no gate). 1.5% risk. Real-time WS price checks. Data feeds ML training.</div>
      {openTrades.length > 0 && <details style={{ marginBottom: 8 }}>
        <summary style={{ fontSize: 9, color: C.mut, textTransform: "uppercase", fontFamily: C.sans, cursor: "pointer" }}>Open Positions ({openTrades.length})</summary>
        <div style={{ marginTop: 6, display: "flex", flexWrap: "wrap", gap: 4 }}>
          {openTrades.map(function(t, i) { return (
            <div key={i} style={{ background: C.inner, borderRadius: 6, padding: "4px 8px", border: "1px solid " + C.bdr, display: "flex", alignItems: "center", gap: 4 }}>
              <span style={{ fontSize: 9, fontWeight: 700, fontFamily: C.mono, color: C.tx }}>{(t.symbol || "").replace("USDT","")}</span>
              <span style={{ fontSize: 8, fontWeight: 700, fontFamily: C.mono, color: t.direction === "LONG" ? C.gn : C.rd }}>{t.direction === "LONG" ? "L" : "S"}</span>
              <span style={{ fontSize: 8, fontFamily: C.mono, color: C.dim }}>{"c" + (t.confidence || "?")}</span>
            </div>
          ); })}
        </div>
      </details>}
      {closedTrades.length > 0 && <details>
        <summary style={{ fontSize: 9, color: C.mut, textTransform: "uppercase", fontFamily: C.sans, cursor: "pointer" }}>Recent Closed ({closedTrades.length})</summary>
        <div style={{ marginTop: 6 }}>
          {closedTrades.map(function(t, i) { return (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "4px 0", borderBottom: "1px solid " + C.inner }}>
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <span style={{ fontSize: 8, fontWeight: 700, fontFamily: C.mono, color: t.outcome === "win" ? C.gn : C.rd, padding: "1px 4px", background: (t.outcome === "win" ? C.gn : C.rd) + "12", borderRadius: 3 }}>{t.outcome === "win" ? "W" : "L"}</span>
                <span style={{ fontSize: 10, fontWeight: 700, fontFamily: C.mono, color: C.tx }}>{(t.symbol || "").replace("USDT","")}</span>
                <span style={{ fontSize: 9, fontFamily: C.mono, color: t.direction === "LONG" ? C.gn : C.rd }}>{t.direction}</span>
              </div>
              <span style={{ fontSize: 10, fontWeight: 700, fontFamily: C.mono, color: (t.pnl || 0) >= 0 ? C.gn : C.rd }}>{((t.pnl || 0) >= 0 ? "+" : "") + "$" + Math.abs(t.pnl || 0).toFixed(2)}</span>
            </div>
          ); })}
        </div>
      </details>}
    </div>}
  </Card>;
}

function CollapsibleSection({ icon, title, color, badge, badgeColor, children, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen || false);
  return <div style={{ marginBottom: 6 }}>
    <div onClick={() => setOpen(!open)} style={{ background: C.card, border: "1px solid " + C.bdr, borderRadius: open ? "12px 12px 0 0" : 12, padding: "14px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", transition: "border-color 0.2s" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 13 }}>{icon}</span>
        <span style={{ fontSize: 12, fontWeight: 700, color: color || C.or, letterSpacing: "1px", textTransform: "uppercase", fontFamily: C.sans }}>{title}</span>
        {badge && <span style={{ display: "inline-flex", padding: "2px 8px", borderRadius: 999, fontSize: 8, fontWeight: 700, fontFamily: C.mono, background: (badgeColor || C.yl) + "15", color: badgeColor || C.yl, border: "1px solid " + (badgeColor || C.yl) + "30" }}>{badge}</span>}
      </div>
      <span style={{ fontSize: 14, color: C.mut, transform: open ? "rotate(90deg)" : "none", transition: "transform 0.2s" }}>{String.fromCharCode(9656)}</span>
    </div>
    {open && <div style={{ borderLeft: "1px solid " + C.bdr, borderRight: "1px solid " + C.bdr, borderBottom: "1px solid " + C.bdr, borderRadius: "0 0 12px 12px", overflow: "hidden" }}>{children}</div>}
  </div>;
}

function SystemStatusBar() {
  const [ml, setMl] = useState(null);
  const [ws, setWs] = useState(null);
  const [expanded, setExpanded] = useState(false);
  useEffect(() => {
    const fetchStatus = async () => {
      const [mRes, wRes] = await Promise.all([api("/api/admin/ml-health"), api("/api/admin/ws-status")]);
      if (mRes.ok) setMl(mRes.data);
      if (wRes.ok) setWs(wRes.data);
    };
    fetchStatus();
    const iv = setInterval(fetchStatus, 15000);
    return () => clearInterval(iv);
  }, []);
  const meta = ml?.model_meta || {};
  const mlOk = ml?.status === "ok";
  const wsOk = ws?.connected;
  return <Card style={{ marginBottom: 10, borderColor: (mlOk && wsOk ? C.gn : C.yl) + "30", cursor: "pointer" }} glow={mlOk && wsOk ? C.gn : C.yl}>
    <div onClick={() => setExpanded(!expanded)}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 13 }}>{String.fromCodePoint(129302)}</span>
            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.5px", color: C.cy, fontFamily: C.sans }}>ML</span>
            <span style={{ display: "inline-flex", padding: "2px 8px", borderRadius: 999, fontSize: 9, fontWeight: 700, fontFamily: C.mono, background: (mlOk ? C.gn : C.rd) + "15", color: mlOk ? C.gn : C.rd, border: "1px solid " + (mlOk ? C.gn : C.rd) + "30" }}>{mlOk ? "Online" : "Offline"}</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 13 }}>{String.fromCodePoint(128225)}</span>
            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.5px", color: C.cy, fontFamily: C.sans }}>WS</span>
            <span style={{ display: "inline-flex", padding: "2px 8px", borderRadius: 999, fontSize: 9, fontWeight: 700, fontFamily: C.mono, background: (wsOk ? C.gn : C.dim) + "15", color: wsOk ? C.gn : C.dim, border: "1px solid " + (wsOk ? C.gn : C.dim) + "30" }}>{ws ? (wsOk ? ws.symbolCount + " symbols" : "Idle") : "..."}</span>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 9, color: C.dim, fontFamily: C.mono }}>{((meta.cv_accuracy || 0) * 100).toFixed(1) + "% acc"}</span>
          <span style={{ fontSize: 14, color: C.mut, transform: expanded ? "rotate(90deg)" : "none", transition: "transform 0.2s" }}>{String.fromCharCode(9656)}</span>
        </div>
      </div>
    </div>
    {expanded && <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid " + C.bdr }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 6, marginBottom: 10 }}>
        {[
          { l: "Accuracy", v: ((meta.cv_accuracy || 0) * 100).toFixed(1) + "%", c: (meta.cv_accuracy || 0) >= 0.5 ? C.gn : C.yl },
          { l: "Features", v: String(meta.feature_count || 0), c: C.tx },
          { l: "Trained On", v: String(meta.trade_count || 0), c: C.tx },
          { l: "WS Symbols", v: String(ws?.symbolCount || 0), c: ws?.symbolCount > 0 ? C.cy : C.dim },
        ].map(x => (
          <div key={x.l} style={{ background: C.inner, borderRadius: 6, padding: 8, border: "1px solid " + C.bdr }}>
            <div style={{ fontSize: 8, color: C.mut, textTransform: "uppercase", fontFamily: C.sans }}>{x.l}</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: x.c, fontFamily: C.mono }}>{x.v}</div>
          </div>
        ))}
      </div>
      {meta.trained_at && <div style={{ fontSize: 9, color: C.dim, fontFamily: C.sans }}>{"Last trained: " + new Date(meta.trained_at).toLocaleString() + " · " + (meta.model_version || "?")}</div>}
      {ws?.symbols?.length > 0 && <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 8 }}>
        {ws.symbols.map(s => <span key={s} style={{ fontSize: 9, fontFamily: C.mono, background: C.cy + "15", color: C.cy, border: "1px solid " + C.cy + "30", borderRadius: 4, padding: "2px 6px" }}>{s.replace("USDT","")}</span>)}
      </div>}
    </div>}
  </Card>;
}

function AuditLog() {
  const [entries, setEntries] = useState([]);
  const [actionTypes, setActionTypes] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAudit = async () => {
      const url = filter === "all" ? "/api/admin/audit?limit=50" : "/api/admin/audit?limit=50&action=" + filter;
      const r = await api(url);
      if (r.ok) {
        setEntries(r.data.entries || []);
        setActionTypes(r.data.actionTypes || []);
      }
      setLoading(false);
    };
    fetchAudit();
    const t = setInterval(fetchAudit, 30000);
    return () => clearInterval(t);
  }, [filter]);

  const actionColor = (a) => {
    if (a.includes("LOGIN_FAILED")) return C.rd;
    if (a.includes("LOGIN") || a.includes("LOGOUT")) return C.bl;
    if (a.includes("TRADE_CLOSED")) return C.yl;
    if (a.includes("TRADE")) return C.gn;
    if (a.includes("USER")) return C.pu;
    if (a.includes("KEYS")) return C.or;
    if (a.includes("SETTINGS") || a.includes("CONFIG")) return C.cy;
    return C.dim;
  };

  const actionIcon = (a) => {
    if (a.includes("LOGIN_FAILED")) return "🚫";
    if (a.includes("LOGIN")) return "🔑";
    if (a.includes("LOGOUT")) return "👋";
    if (a.includes("TRADE_CLOSED")) return "📤";
    if (a.includes("TRADE_AUTO")) return "🤖";
    if (a.includes("TRADE_MANUAL")) return "👆";
    if (a.includes("USER_ADDED")) return "➕";
    if (a.includes("USER_REMOVED")) return "➖";
    if (a.includes("KEYS")) return "🔐";
    if (a.includes("SETTINGS")) return "⚙";
    if (a.includes("CONFIG")) return "📡";
    return "📋";
  };

  const formatTime = (ts) => {
    const d = new Date(ts);
    const now = new Date();
    const diff = now - d;
    if (diff < 60000) return "just now";
    if (diff < 3600000) return Math.floor(diff / 60000) + "m ago";
    if (diff < 86400000) return Math.floor(diff / 3600000) + "h ago";
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  };

  const formatDetails = (entry) => {
    const parts = [];
    if (entry.symbol) parts.push(cleanSym(entry.symbol));
    if (entry.direction) parts.push(entry.direction);
    if (entry.pnl != null) parts.push((entry.pnl >= 0 ? "+$" : "-$") + Math.abs(entry.pnl).toFixed(2));
    if (entry.outcome) parts.push(entry.outcome);
    if (entry.confidence) parts.push("conf:" + entry.confidence);
    if (entry.newUser) parts.push(entry.newUser);
    if (entry.removedUser) parts.push(entry.removedUser);
    if (entry.ip) parts.push(entry.ip);
    return parts.join(" · ");
  };

  return (
    <Card style={{ marginBottom: 14 }}>
      <Stitle icon="📋" color={C.cy}>Audit Log</Stitle>
      <div style={{ display: "flex", gap: 4, marginBottom: 12, flexWrap: "wrap" }}>
        {["all", "LOGIN", "TRADE_AUTO", "TRADE_CLOSED", "SETTINGS_CHANGED", "LOGIN_FAILED"].map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{
            padding: "5px 10px", background: filter === f ? C.or + "15" : C.inner,
            border: "1px solid " + (filter === f ? C.or : C.bdr), borderRadius: 6,
            color: filter === f ? C.or : C.dim, cursor: "pointer",
            fontFamily: C.sans, fontSize: 9, fontWeight: filter === f ? 700 : 500,
          }}>{f === "all" ? "All" : f.replace(/_/g, " ")}</button>
        ))}
      </div>
      {loading ? <Loader text="Loading audit log..." /> : entries.length === 0 ? (
        <div style={{ textAlign: "center", padding: 20, color: C.dim, fontFamily: C.sans, fontSize: 12 }}>No audit entries yet</div>
      ) : (
        <div style={{ maxHeight: 400, overflowY: "auto" }}>
          {entries.map((e, i) => (
            <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "8px 0", borderBottom: "1px solid " + C.inner }}>
              <span style={{ fontSize: 14, flexShrink: 0, marginTop: 1 }}>{actionIcon(e.action)}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 9, fontWeight: 700, color: actionColor(e.action), fontFamily: C.mono, textTransform: "uppercase" }}>{e.action}</span>
                  <span style={{ fontSize: 10, fontWeight: 600, color: C.tx, fontFamily: C.mono }}>{e.username}</span>
                </div>
                {formatDetails(e) && <div style={{ fontSize: 9, color: C.dim, fontFamily: C.sans, marginTop: 2 }}>{formatDetails(e)}</div>}
              </div>
              <span style={{ fontSize: 9, color: C.mut, fontFamily: C.sans, flexShrink: 0, whiteSpace: "nowrap" }}>{formatTime(e.timestamp)}</span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );

}


const NAV = [
  { id: "market", Icon: TrendingUp, label: "Market" },
  { id: "signals", Icon: Zap, label: "Signals" },
  { id: "ai", Icon: Brain, label: "AI" },
  { id: "trades", Icon: Briefcase, label: "Live" },
  { id: "journal", Icon: BookOpen, label: "Journal" },
  { id: "settings", Icon: SettingsIcon, label: "Settings" },
  { id: "admin", Icon: Crown, label: "Admin", adminOnly: true },
];

// ─── Main App ────────────────────────────────────────
export default function App() {
  const [authed, setAuthed] = useState(!!getToken());
  const [currentUser, setCurrentUser] = useState(getUser());
  const [userRole, setUserRole] = useState("");
  const [tab, setTab] = useState("market");
  const [scan, setScan] = useState(null);
  const [trades, setTrades] = useState([]);
  const [tickers, setTickers] = useState([]);
  const [config, setConfig] = useState({ accountBalance: 1000, scanPairsLimit: 50, maxOpenTrades: 3 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [bitget, setBitget] = useState(null);
  const [bybit, setBybit] = useState(null); // PATCHED: LiveBalance v1
  const [userPrefs, setUserPrefs] = useState(null); // PATCHED: UIFixes v2 — user's exchange + maxOpenTrades
  const timer = useRef(null);
  const bgTimer = useRef(null);

  const handleLogin = (username, role) => { setAuthed(true); setCurrentUser(username); setUserRole(role); };
  const handleLogout = async () => { await api("/api/logout", { method: "POST" }); clearToken(); setAuthed(false); setCurrentUser(""); };

  const fetchAll = useCallback(async (force = false) => {
    setLoading(true); setError(null);
    try {
      const [sr, tr, st] = await Promise.all([api(`/api/scan${force ? "?force=1" : ""}`), api("/api/trades?limit=300"), api("/api/status")]);
      if (sr._authFailed || tr._authFailed || st._authFailed) { setAuthed(false); clearToken(); return; }
      if (sr.ok) { setScan(sr.data); setTickers(sr.data?.topPairs || []); } else setError(sr.error);
      if (tr.ok) setTrades(tr.data || []);
      if (st.ok) { setConfig(p => ({ ...p, ...st.config })); if (st.user) setCurrentUser(st.user); if (st.role) setUserRole(st.role); }
    } catch (e) { setError(e.message); }
    setLoading(false);
  }, []);

  const fetchBitget = useCallback(async () => {
    const r = await api("/api/bitget/summary");
    if (r.ok && r.data) setBitget(r.data);
  }, []);
  const fetchBybit = useCallback(async () => {
    const r = await api("/api/bybit/summary");
    if (r.ok && r.data) setBybit(r.data); else setBybit(null);
  }, []);
  const fetchUserPrefs = useCallback(async () => {
    const r = await api("/api/my-settings");
    if (r.ok && r.data) setUserPrefs(r.data);
  }, []);

  useEffect(() => {
    if (!authed) return;
    fetchAll(false);
    fetchBitget();
    fetchBybit();
    fetchUserPrefs();
    timer.current = setInterval(() => fetchAll(false), REFRESH_MS);
    bgTimer.current = setInterval(() => { fetchBitget(); fetchBybit(); fetchUserPrefs(); }, BITGET_REFRESH_MS);
    return () => { clearInterval(timer.current); clearInterval(bgTimer.current); };
  }, [authed, fetchAll, fetchBitget, fetchBybit, fetchUserPrefs]);

  const exec = useCallback(async s => {
    const execRes = await api("/api/execute", { method: "POST", body: JSON.stringify({ signal: s }) });
    if (execRes.ok) { setTrades(p => [...p, execRes.data.trade]); setTab("trades"); fetchBitget(); return; }
    if (execRes.error && !execRes.error.includes("API keys")) { alert(`Execution failed: ${execRes.error}`); return; }
    const r = await api("/api/trades", { method: "POST", body: JSON.stringify({ action: "open", trade: { symbol: s.symbol, exchange: s.exchange, assetType: s.assetType, direction: s.direction, entry: s.entry, stopLoss: s.stopLoss, tp1: s.tp1, tp2: s.tp2, tp3: s.tp3, leverage: s.leverage, riskPct: s.riskPct, riskAmount: s.riskAmount, positionSize: s.positionSize, notional: s.notional, confidence: s.confidence, confirmations: s.confirmations } }) });
    if (r.ok) { setTrades(p => [...p, r.data]); setTab("trades"); }
  }, [fetchBitget]);

  const close = useCallback(async (id, outcome, pnl) => {
    const r = await api("/api/trades", { method: "POST", body: JSON.stringify({ action: "close", trade: { id, outcome, pnl } }) });
    if (r.ok) { setTrades(p => p.map(t => t.id === id ? { ...t, ...r.data } : t)); const s = await api("/api/status"); if (s.ok) setConfig(p => ({ ...p, ...s.config })); fetchBitget(); }
  }, [fetchBitget]);

  const saveCfg = useCallback(async u => { const r = await api("/api/config", { method: "POST", body: JSON.stringify(u) }); if (r.ok) setConfig(p => ({ ...p, ...r.data })); }, []);
  const reset = useCallback(async () => { await api("/api/reset", { method: "POST" }); setTrades([]); setScan(null); setConfig(p => ({ ...p, accountBalance: 1000 })); fetchAll(false); }, [fetchAll]);

  if (!authed) {
    return <>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700;800&family=Space+Grotesk:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
      <LoginScreen onLogin={handleLogin} />
      <style>{`* { box-sizing: border-box; } body { margin: 0; } input::placeholder { color: ${C.mut}; } input:focus { border-color: ${C.or}60 !important; }`}</style>
    </>;
  }

  // PATCHED: Frontend-v2 — initial-load loader so user doesn't see $1000/0/3 defaults flash
  if (!userPrefs) {
    return <>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700;800&family=Space+Grotesk:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
      <div style={{ width: "100%", minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 22, fontWeight: 800, fontFamily: C.display, letterSpacing: "-0.3px", marginBottom: 20, color: C.tx }}>
            bit<span style={{ color: C.or }}>scanner</span>
          </div>
          <Loader text="Loading dashboard..." />
        </div>
      </div>
      <style>{`@keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:0.3; } } body { margin: 0; }`}</style>
    </>;
  }

  // PATCHED: Frontend-v2 — exchange-aware position count
  const oc = (userPrefs?.exchange === "Bybit" ? bybit?.positionCount : bitget?.positionCount) ?? trades.filter(t => t.status === "open" && t.executedBy === currentUser).length;
  const liveEquity = bybit?.equity || bitget?.equity || config.accountBalance || 0; // PATCHED: LiveBalance v1
  const livePnl = userPrefs?.exchange === "Bybit" ? (bybit?.unrealizedPL || 0) : (bitget?.unrealizedPL || 0);

  return (
    <div style={{ width: "100%", minHeight: "100vh", background: C.bg, color: C.tx, fontFamily: C.sans, paddingBottom: 72 }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700;800&family=Space+Grotesk:wght@400;500;600;700;800&display=swap" rel="stylesheet" />

      {/* ─── Sticky Header ─── */}
      <div style={{ position: "sticky", top: 0, zIndex: 90, borderBottom: `1px solid ${C.bdr}`, padding: "10px 16px", background: `${C.bg}ee`, backdropFilter: "blur(12px)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 7, height: 7, borderRadius: "50%", background: error ? C.rd : C.gn, boxShadow: `0 0 8px ${error ? C.rd : C.gn}60`, animation: "pulse 2s infinite" }} />
          <span style={{ fontSize: 14, fontWeight: 800, fontFamily: C.display, letterSpacing: "-0.3px" }}>
            bit<span style={{ color: C.or }}>scanner</span>
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: C.or, fontFamily: C.mono }}>${liveEquity.toFixed(2)}</div>
            {livePnl !== 0 && <div style={{ fontSize: 10, fontWeight: 700, color: livePnl >= 0 ? C.gn : C.rd, fontFamily: C.mono }}>{livePnl >= 0 ? "+" : ""}${livePnl.toFixed(2)}</div>}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <div style={{ padding: "4px 8px", background: C.inner, borderRadius: 6, border: `1px solid ${C.bdr}`, fontSize: 10, fontWeight: 700, color: C.dim, fontFamily: C.mono }}>{oc}/{userPrefs?.maxOpenTrades || 5}</div>
            <button onClick={() => { fetchAll(false); fetchBitget(); }} disabled={loading} style={{
              padding: "6px 10px", background: loading ? C.inner : `${C.or}12`,
              border: `1px solid ${loading ? C.bdr : C.or}40`, color: loading ? C.mut : C.or,
              borderRadius: 6, cursor: loading ? "wait" : "pointer", fontFamily: C.mono, fontSize: 10, fontWeight: 700, transition: "all 0.2s",
            }}>{loading ? "···" : "⟳"}</button>
          </div>
        </div>
      </div>

      {error && !loading && <div style={{ margin: "8px 16px 0", padding: "10px 14px", background: `${C.rd}08`, border: `1px solid ${C.rd}20`, borderRadius: 10, fontSize: 11, color: C.rd, fontFamily: C.sans }}>⚠ {error}</div>}

      <div style={{ padding: 16, maxWidth: 960, margin: "0 auto" }}>
        {tab === "market" && <MarketTab scan={scan} loading={loading && !scan} />}
        {tab === "signals" && <SignalsTab scan={scan} loading={loading && !scan} onExec={exec} openCount={oc} maxOpen={config.maxOpenTrades || 3} />}
        {tab === "ai" && <AITab fearGreed={scan?.marketSentiment?.fearGreed} scan={scan} />}
        {tab === "trades" && <LiveTradesTab bitget={bitget} bybit={bybit} userExchange={userPrefs?.exchange || "Bitget"} userMaxTrades={userPrefs?.maxOpenTrades || 5} />}
        {tab === "journal" && <JournalTab trades={trades} bitget={bitget} bybit={bybit} userExchange={userPrefs?.exchange || "Bitget"} currentUser={currentUser} />}
        {tab === "admin" && userRole === "admin" && <AdminTab />}
        {tab === "settings" && <SettingsTab config={config} onSave={saveCfg} onReset={reset} onLogout={handleLogout} currentUser={currentUser} userRole={userRole} balance={liveEquity} />}
      </div>

      {/* ─── Bottom Nav ─── */}
      <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, background: `${C.bg}ee`, backdropFilter: "blur(12px)", borderTop: `1px solid ${C.bdr}`, display: "flex", justifyContent: "space-around", padding: "8px 0 10px", zIndex: 100 }}>
        {NAV.filter(n => !n.adminOnly || userRole === "admin").map(n => {
          const active = tab === n.id;
          const count = n.id === "signals" ? (scan?.setups?.length || 0) : n.id === "trades" ? oc : 0;
          return <button key={n.id} onClick={() => setTab(n.id)} style={{ background: "none", border: "none", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 3, padding: "4px 12px", position: "relative", minWidth: 56, transition: "all 0.15s" }}>
            <n.Icon size={20} strokeWidth={active ? 2.4 : 1.8} color={active ? C.or : C.dim} style={{ transition: "all 0.2s" }} />
            <span style={{ fontSize: 11, color: active ? C.or : C.dim, fontWeight: active ? 700 : 500, fontFamily: C.sans, transition: "all 0.2s" }}>{n.label}</span>
            {count > 0 && <span style={{ position: "absolute", top: -2, right: 0, background: C.rd, color: "#fff", borderRadius: 10, minWidth: 16, height: 16, fontSize: 8, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, padding: "0 4px" }}>{count}</span>}
            {active && <div style={{ position: "absolute", top: -7, width: 20, height: 3, background: C.or, borderRadius: 2 }} />}
          </button>;
        })}
      </div>

      <style>{`
        @keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:0.3; } }
        * { box-sizing: border-box; -webkit-tap-highlight-color: transparent; }
        input::placeholder { color: ${C.mut}; }
        input:focus { border-color: ${C.or}60 !important; outline: none; }
        ::-webkit-scrollbar { width:4px; height:4px; }
        ::-webkit-scrollbar-track { background:${C.bg}; }
        ::-webkit-scrollbar-thumb { background:${C.bdr}; border-radius:4px; }
        body { margin: 0; overscroll-behavior: none; touch-action: manipulation; }
        details summary::-webkit-details-marker { display: none; }
        details summary { list-style: none; }
        details summary::before { content: "▸ "; color: ${C.or}; }
        details[open] summary::before { content: "▾ "; }
      `}</style>
    </div>
  );
}
